/**
 * MetaCommand — typed API client
 * All fetch() calls go through this module.
 */

import type { ProjectedState } from '@/types/game'
import type { NewCardLandReason, SynergyPresence, SynergyReceipt } from '@/lib/rankingTerms'

export async function apiFetch(url: string, options: RequestInit = {}): Promise<Response> {
  return fetch(url, { credentials: 'same-origin', ...options });
}

export class ApiError extends Error {
  status: number;
  retryAfter: number | null;

  constructor(message: string, status: number, retryAfter: number | null = null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.retryAfter = retryAfter;
  }
}

/** The backend's 5xx body (metacommand/web/errors.py): a user-safe message ending "(ref <id>)". */
function isSafeServerError(body: unknown): body is { detail: string; error_ref: string } {
  if (typeof body !== 'object' || body === null) return false;
  const b = body as Record<string, unknown>;
  return typeof b.detail === 'string' && typeof b.error_ref === 'string';
}

export async function apiError(r: Response, fallback: string): Promise<never> {
  const body = await r.json().catch(() => undefined);   // undefined: not JSON
  // A 5xx shows only the backend's safe shape; any other 5xx body (exception text, a proxy
  // page) is never shown — the caller's own fallback reads in its place.
  if (r.status >= 500) {
    throw new ApiError(isSafeServerError(body) ? body.detail : fallback, r.status);
  }
  let detail = fallback;
  if (typeof body === 'object' && body !== null) {
    const raw = body.detail ?? body.error ?? body.message ?? fallback;
    // FastAPI validation errors return detail as an array of objects
    if (Array.isArray(raw)) {
      detail = raw.map((e: { msg?: string }) => e.msg ?? String(e)).join('; ');
    } else if (typeof raw === 'object' && raw !== null) {
      // Structured 422 details ({card|remove_card, reason}) read as "<card>: <reason>".
      const subject = raw.remove_card ?? raw.card;
      detail = raw.msg ?? raw.message
        ?? (typeof raw.reason === 'string' ? (subject ? `${subject}: ${raw.reason}` : raw.reason) : JSON.stringify(raw));
    } else {
      detail = String(raw);
    }
  }
  const retryAfter = r.status === 429 ? parseInt(r.headers.get('Retry-After') ?? '') || null : null;
  throw new ApiError(detail, r.status, retryAfter);
}

export async function ok<T>(r: Response, fallback: string): Promise<T> {
  if (!r.ok) return apiError(r, fallback);
  return r.json() as Promise<T>;
}

// ─── Types ────────────────────────────────────────────────────────────────────

export interface CardEntry {
  qty: number;
  scryfall_id?: string;
  set?: string;
  cn?: string;
  finish?: string;
  printings?: Array<{ qty: number; scryfall_id: string; set?: string; cn?: string; finish: string }>;
}

export type TagGroupKind = 'role' | 'theme' | 'user' | 'land' | 'other';

/** One deck-view tag group, computed by the backend (metacommand/deck/tag_grouping.py). */
export interface TagGroup {
  label: string;
  kind: TagGroupKind;
  /** Tagger slug for a theme, role anchor for a role; null otherwise. */
  tag: string | null;
  cards: string[];
  /** Members present by user tag — the only rows Edit Tags can remove from this group. */
  user_cards: string[];
}

export interface CardMeta {
  types: string[];
  subtypes: string[];
  supertypes: string[];
  manaCost: string;
  manaValue: number;
  type: string;
  tags: string[];
  oracle_text: string;
  /** Scryfall produced_mana: colors of mana this card can produce (W/U/B/R/G/C). */
  produced_mana?: string[];
  game_changer?: boolean;
  power?: string;
  toughness?: string;
  colors?: string[];
  colorIdentity?: string[];
  banned?: boolean;
  unreleased?: boolean;
  keywords?: string[];
  rarity?: string;
  set_code?: string;
  set_name?: string;
  price_usd?: number | null;
  price_usd_foil?: number | null;
  reprint?: boolean;
  in_deck?: boolean;
  /** Scryfall layout: 'normal' | 'split' | 'flip' | 'transform' | 'modal_dfc' | 'adventure' | 'meld' | … */
  layout?: string | null;
}

export interface User {
  id: string;
  email: string;
  display_name: string;
  avatar_url?: string;
  is_admin: boolean;
  role: 'user' | 'influencer' | 'admin';
  created_at?: string;
  elo?: number;
}

export interface Deck {
  id: string;
  name: string;
  commanders?: string[];
  format?: string;
  visibility?: 'public' | 'private';
  /** Owner opt-in: the deck's change history rides in its primer. */
  history_in_primer?: boolean;
  user_id?: string;
  owner_id?: string;
  display_name?: string;
  avatar_url?: string;
  is_admin?: boolean;
  role?: string;
  card_count?: number;
  source_url?: string;
  /** e.g. 'manual', 'moxfield', 'variant_materialized' — present on list + detail payloads. */
  source_type?: string;
  /** Raw JSON string from the DB. Parse with parseDeckContext() before use. */
  deck_context?: string | null;
  created_at?: string;
  updated_at?: string;
  color_identity?: string | string[];
  /** The builder's target bracket — never the shown label (that is latest_analysis.power_level.wotc_bracket). */
  bracket_override?: number | null;
  /** The owner's bracket declaration: Exhibition (B1, when the deck breaks no B1 rule) or cEDH (B5). */
  bracket_declaration?: BracketDeclarationKind | null;
  speed_category?: string;
  cards?: Record<string, CardEntry>;
  latest_analysis?: AnalysisTaskResult | null;
  /** The deck's themes, computed server-side: the builder's chosen themes once seeded, else the
   *  latest analysis' discovered ones. Listing payloads only. */
  themes?: SynergyTheme[];
  /** The deck's fresh What's New, listing payloads only; null when absent or stale. */
  new_cards_summary?: NewCardsSummary | null;
  banner_position?: string;
  game_stats?: DeckGameStats;
  /** What the server's source sync did on this load. Deck detail only; every viewer gets
   *  it, but only the owner's load actually syncs — others read `skipped`/`not_owner`. */
  source_sync?: SourceSync;
}

/**
 * Outcome of the server-side source sync that runs when a deck's owner loads it.
 * `counts` read in the applying direction: `added` is what the sync would add to the deck.
 */
export interface SourceSync {
  status: 'applied' | 'incoming' | 'up_to_date' | 'skipped' | 'pending' | 'error';
  checked_at?: string | null;
  reason?: string | null;
  counts?: { added: number; removed: number; changed: number } | null;
  analysis_started: boolean;
  /** The sync took the source's new deck name (a rename can land without a list change). */
  renamed: boolean;
  /** The source's new name for the deck, kept out because the owner chose the deck's name. */
  incoming_name?: string | null;
}

/** Result of applying the fetched source list (`POST /decks/{id}/apply/source`). */
export interface ApplySourceResult {
  applied: boolean;
  /** The deck took the source's name. */
  renamed: boolean;
  /** The source's name, kept out because the owner chose the deck's name. */
  incoming_name?: string | null;
}

/** Parse a Deck's `deck_context` JSON string safely. Returns null on failure. */
export function parseDeckContext(deck: Deck): Record<string, unknown> | null {
  if (!deck.deck_context) return null;
  if (typeof deck.deck_context === 'object') return deck.deck_context as Record<string, unknown>;
  try {
    return JSON.parse(deck.deck_context) as Record<string, unknown>;
  } catch {
    return null;
  }
}

export interface DiscoveredDeck {
  name: string;
  url: string;
  deck_id?: string;
  format?: string;
  commander?: string | null;
  already_imported: boolean;
}

/** Fields every task carries, analysis or system alike. */
export interface TaskBase {
  task_id: string;
  phase: string;
  phase_label: string;
  is_active: boolean;
  progress_pct: number;
  created_at: number;
  completed_at: number | null;
}

/** An analysis-lane task: deck power-level/optimization, tuning, new-cards, Dec-a-Sketch. */
export interface AnalysisTaskData extends TaskBase {
  task_type?: 'analysis' | 'tuning' | 'new_cards' | 'das';
  deck_id: string;
  deck_name: string;
  retry_after?: number;
  stale?: boolean;
  stale_analyzed_at?: string | null;
  /** Build the task ran against: 'base' or a variant id. */
  build_id?: string;
  /** Set when phase is 'failed': a user-safe message, never exception text. A generic message
   * ending "(ref <id>)" (the id finds the logged exception), or a fixed condition such as
   * 'Build not found'. */
  error?: string | null;
  result?: AnalysisTaskResult;
}

/** A system-lane task: import, Tagger import, classify backlog, rulings/rules import, prune, Spellbook combo sync,
 * the What's New feed, the recent-sets refresh.
 * Has no deck and no creator when run by the scheduler (`owner: 'system'`). */
export interface SystemTaskData extends TaskBase {
  task_type: 'system';
  kind: 'import' | 'tagger_import' | 'classify_backlog' | 'rulings_import' | 'rules_import' | 'prune' | 'edhrec_warm' | 'combo_import' | 'combo_backfill' | 'corpus_import' | 'theme_page_warm' | 'whats_new_feed' | 'recent_sets' | 'theme_harvest';
  owner: string;
  units_done: number;
  units_total: number | null;
  unit: string;
  message: string;
  /** Set when phase is 'failed': the raw exception text. Admin-only, as system tasks are. */
  error?: string | null;
  result: Record<string, number | boolean | string[] | null> | null;
}

export type TaskData = AnalysisTaskData | SystemTaskData;

/** GET /admin/combos/status: the Commander Spellbook bulk sync's state. */
export interface CombosStatus {
  last_synced_at: string | null;
  /** True when the last successful sync is more than 7 days old (detection then uses the network). */
  is_stale: boolean;
  version: string | null;
  export_timestamp: string | null;
  variant_count: number | null;
}

/** Where a shown-bracket change comes from: the deploy itself, the new combo rows, or both. */
export type ComboBackfillSource = 'rules' | 'backfill' | 'rules+backfill';

/** One deck in the bracket-change review (backend-computed: the frontend only renders it). */
export interface ComboBackfillChange {
  analysis_id: number;
  deck_id: string;
  deck_name: string;
  owner: string | null;
  /** Shown today (frame 3b): the higher of the rule floor and the stored x/10 estimate. */
  current: number | null;
  /** Shown after the deploy, before the backfill. */
  rules: number | null;
  /** Shown after the deploy and the backfill. */
  after: number | null;
  floor_rules: number | null;
  floor_after: number | null;
  /** null on a floor-only change. */
  source: ComboBackfillSource | null;
  /** The floor_reasons wire, after. */
  reasons: FloorReason[];
  /** The plays_like_reasons wire, after. */
  plays_like: PlaysLikeReason[];
}

/** A bracket-change review / combo backfill report: PREVIEW (writes nothing) or APPLY. */
export interface ComboBackfillReport {
  mode: 'preview' | 'apply';
  generated_at: string;
  seconds: number;
  fingerprint: string;
  analyses: number;
  targets: number;
  with_combos: number;
  analyses_written: number;
  shown_changes: ComboBackfillChange[];
  floor_only_changes: ComboBackfillChange[];
  unresolved: string[];
  network_failed: string[];
  still_combo_blind: string[];
  cancelled: boolean;
  error: string | null;
}

/** GET /admin/combos/backfill: each mode's last report (null when never run). */
export interface ComboBackfillReports {
  preview: ComboBackfillReport | null;
  apply: ComboBackfillReport | null;
}

export interface DeckGameStats {
  wins: number;
  losses: number;
  draws: number;
  total: number;
}

export interface LeaderboardEntry {
  id: string;
  display_name: string;
  avatar_url: string | null;
  elo: number;
  role: string;
}

export interface BillingStatus {
  subscription_status: string;
  analysis_credits_used: number;
  analysis_limit: number;
  credits_remaining: number;
  is_pro: boolean;
  role: string;
}

export interface AdminUser {
  id: string;
  email: string;
  display_name: string;
  avatar_url?: string;
  role: string;
  roles?: string[];
  is_admin: boolean;
  subscription_status: string;
  analysis_credits_used: number;
  created_at?: string;
  last_login?: string;
  deck_count?: number;
}

export interface AdminFeatureFlag {
  flag_name: string;
  role: string;
  enabled: boolean;
}

export interface LLMUsageStat {
  phase: string;
  task_type: string;
  count: number;
  avg_duration_seconds: number | null;
  total_input_tokens: number;
  total_output_tokens: number;
  total_cache_read_tokens: number;
  avg_input_tokens: number | null;
  avg_output_tokens: number | null;
  total_cost_usd: number | null;
  avg_cost_usd: number | null;
  avg_llm_calls: number | null;
}

export interface LLMUsageRecord {
  id: number;
  task_id: string | null;
  deck_id: string | null;
  phase: string;
  task_type: string;
  model: string | null;
  input_tokens: number;
  output_tokens: number;
  cache_read_tokens: number;
  llm_calls: number;
  cost_usd: number | null;
  duration_seconds: number | null;
  created_at: string;
}

export interface AnalysisResult {
  id: string;
  deck_id: string;
  deck_name: string;
  analysis_type?: string;
  created_at?: string;
  power_level?: PowerLevelResult;
  optimization?: OptimizationResult;
}

export interface ProfileUser {
  id: string;
  email: string;
  display_name: string;
  avatar_url?: string;
  role: string;
  created_at?: string;
  elo?: number;
}

export interface ProfileDeck {
  id: string;
  name: string;
  commanders?: string[];
  card_count?: number;
  format?: string;
  visibility?: string;
  color_identity?: string | string[];
  updated_at?: string;
  latest_analysis?: { power_level?: Pick<PowerLevelResult, BracketFieldKey | 'mana_curve' | 'combo_analysis' | 'estimated_win_turn' | 'power_indicators'> } | null;
  game_stats?: DeckGameStats;
}

export type HomeFacet = 'speed' | 'consistency' | 'interaction' | 'closing';

/** One facet as its chip's tooltip reads it: the deck's level before and after the card (facet points, 0-10),
 *  the target the closure was measured against (null: no goal and no usable fence) and where that target came
 *  from (`bracket`: the fence bracket a median came from; null for a goal or when the source is unknown). */
export interface FacetLevel {
  before: number;
  after: number;
  target: number | null;
  source: 'goal' | 'fence_median' | null;
  bracket: number | null;
}

export type CoplayAbstainReason = 'no_column' | 'low_coverage' | 'no_hits';

/** Co-play looked at this deck and gave no score. `neighbors` (no_hits only): the cards the corpus DOES play
 *  the candidate with, strongest first. */
export interface CoplayAbstained {
  reason: CoplayAbstainReason;
  neighbors: string[];
}

/** One plan entry the card pulls toward (or drags against), with the archetype / commander sources behind it. */
export interface PlanSourceWire {
  kind: 'archetype' | 'commander';
  name: string;
  ratio: number;
  /** The backend-composed sentence for why the card fits, shown verbatim ("fits <archetype name>" or "fits the commander's plan"). */
  reason: string;
}
export interface PlanPull { entry: string; value: number; sources: PlanSourceWire[] }
export interface PlanReceipts { pulls: PlanPull[]; drags: PlanPull[] }

/** The deck's plan on the wire: its archetypes by display name; archetypes sharing a name are one entry (weights summed). null when the deck has no archetype. */
export interface PlanSummary {
  archetypes: { name: string; named_by: 'edhrec' | 'tagger'; weight: number; tags: string[] }[];
  notes: [string, string][];
}

export interface HomeRecommendation {
  deck_id: string;
  deck_name: string;
  commander: string;
  score: {
    total_closure: number;
    lens_score?: number;
    gap_closure: Record<HomeFacet, number>;
    synergy: number;
    synergy_presence: SynergyPresence;
    /** The weight the lens applied to synergy: the admin co-play weight for 'coplay', 0.5 otherwise. */
    synergy_weight: number;
    /** Present only when synergy_presence is 'estimated' (tag pairs) or 'coplay' ([deck card name, 'co-play']). */
    synergy_receipts?: SynergyReceipt[];
    plan_fit: number;
    plan_receipts?: PlanReceipts | null;
    // Wire strings from DeckRanking. budget_fit never produces 'flagged' today; the union is the contract.
    budget_fit: 'ok' | 'strain' | 'flagged';
    salt_fit: 'ok' | 'strain' | 'flagged';
    degraded: boolean;
    /** What justifies each facet chip's number. */
    facet_levels: Record<HomeFacet, FacetLevel>;
    /** null: co-play scored this deck (presence 'coplay'), or co-play is off / has no model. */
    coplay_abstained: CoplayAbstained | null;
  };
  receipts: Record<string, string[]>;
  advisory: { bracket: number; position: string; n: number } | null;
  /** The review's audited sentence for a kept deck; null when unreviewed or dropped. No cut list: the swap
   *  proposal (GET /decks/{id}/build/swap) is the one cut ranker. */
  why: string | null;
}

export interface HomeExcludedDeck {
  deck_id: string;
  deck_name: string;
  reason: string;
  /** The review's reason, present when reason is 'review_veto'. */
  review_reason?: string;
  total_closure: number;
  synergy: number;
  synergy_presence: SynergyPresence;
  /** The weight the lens applied to synergy (see HomeRecommendation.score.synergy_weight). */
  synergy_weight: number;
}

export interface FindCardHomeResult {
  card: {
    name: string;
    oracle_id?: string | null;
    type_line: string;
    oracle_text: string;
    mana_cost: string;
    color_identity: string[];
  };
  filtered_ci: number;
  filtered_has_card: number;
  eligible_count: number;
  recommendations?: HomeRecommendation[];
  excluded?: HomeExcludedDeck[];
  analysis?: string | null;
  review_skipped?: 'credits' | 'failed' | null;
  eligible_decks?: { id: string; name: string }[];
}

export interface DeckConcept {
  commander: string;
  title: string;
  strategy: string;
  key_synergies: string[];
  /** Exact card names the concept proposes as seeds; the server vets each one. */
  seed_cards: string[];
  /** Stored-theme slugs the build tops up from (at most 2, validated server-side). */
  fill_themes?: string[];
  /** Card names in the concept prose that fall outside the commander's colour identity. */
  offcolor_refs?: string[];
}

export interface DecASketchConceptsResult {
  concepts: DeckConcept[];
  bracket: number;
}

/** Returned immediately by the non-blocking build endpoint. */
export interface DecASketchBuildStarted {
  run_id: number;
}

export interface AnalysisRun {
  task_id: string;
  deck_id: string;
  deck_name: string;
  owner: string;
  task_type: string;
  phases: string[];
  total_duration: number;
  sum_duration: number;
  total_input_tokens: number;
  total_output_tokens: number;
  started_at: string;
  last_phase_at: string;
}

/** What happened to one proposed seed card (wire values of `SeedOutcome` in sketch_fill.py). */
export type SeedOutcome =
  | 'included' | 'land_included' | 'not_found' | 'duplicate' | 'color_identity' | 'not_legal'
  | 'no_role_fit' | 'land_held' | 'pip_uncastable';

export interface DasSeedReport {
  requested: string;
  name: string | null;
  outcome: SeedOutcome;
  /** A land-reason value for a held land; the missing pip (e.g. 'C', 'G/W') for pip_uncastable; null otherwise. */
  reason: NewCardLandReason | 'repeated seed' | 'unscored' | string | null;
}

export interface DasRoleFill { role: string; target: number; filled: number }

/** The goals frame's inspection wire form plus the build's bracket and the envelope it was measured against. */
export type DasFitReport = Omit<GoalsInspection, 'build_id' | 'trial' | 'goals'> & {
  bracket: number;
  goals: GoalEnvelope | null;
};

/** A completed deterministic sketch. `notes` is null when the narration failed or was unusable. */
export interface DasResult {
  decklist: { name: string; qty: number }[];
  commander: string;
  seed_reports: DasSeedReport[];
  role_fill: DasRoleFill[];
  shortfalls: string[];
  fit_report: DasFitReport;
  total_price_usd: number;
  unpriced_count: number;
  /** The mana base's share of total_price_usd; exempt from the deck budget. */
  land_price_usd: number;
  notes: string | null;
  log: string[];
  /** Theme slugs the build topped up from. */
  themes?: string[];
  /** How many of the commander's most-played EDHREC cards the sketch holds; null when EDHREC has no page. */
  edhrec_overlap?: { n: number; of: number } | null;
  /** Backend-written operator notes for themes whose stored pool was thin. */
  theme_pool_notes?: string[];
  /** Castability numbers for the deck's own curve; absent on results stored before it existed. */
  curve_report?: DasCurveReport;
}

export interface DasCurveReport {
  avg_mv: number;
  horizon: number;
  ramp_shift: number;
  /** Joint probability of at least 3 land drops AND at least 3 castable nonland cards by the horizon turn. */
  p_development: number;
  /** Probability of drawing an answer within the horizon. */
  p_solution: number;
  expected_castable_by_turn: Record<string, number>;
  /** Pips with only 1-2 land sources; empty when none, absent on older results. */
  thin_pips?: string[];
}

export interface DasRun {
  id: number;
  user_id: string;
  commander: string;
  strategy: string;
  bracket: number;
  budget_usd?: number | null;
  notes: string | null;
  unknown_cards: string[];
  optimization_log: string[];
  /** The structured result; null for a run stored before the deterministic rework. */
  result?: DasResult | null;
  saved_deck_id: string | null;
  outcome: 'pending' | 'saved' | 'rejected';
  status: 'pending' | 'running' | 'complete' | 'error';
  created_at: string;
  cards?: Record<string, number>;
}

/** The envelope fields a sketch build request carries; null = none (never 0, which means $0). */
export interface DecASketchEnvelope {
  deck_budget_usd: number | null;
  budget_per_card_usd: number | null;
}

export interface CommanderStat {
  wins: number;
  losses: number;
  draws: number;
  total: number;
}

export interface ProfileData {
  user: ProfileUser;
  decks: ProfileDeck[];
  deck_count: number;
  is_own_profile: boolean;
  pod_games?: PodracerGame[];
  roast?: string | null;
  roast_published: boolean;
  commander_stats?: Record<string, CommanderStat>;
}

export interface DeckNameIdeas {
  names: string[];
  /** The deck's name at Moxfield/Archidekt; picking it lets later source renames flow in again. */
  source_name: string | null;
  cached: boolean;
}

/** The tuner is driven from the Build tab: it tunes ONE build toward that build's bracket. */
export interface TuneParams {
  target_bracket: number;
  build_id: string;
}

export interface NewCardSetInfo {
  code: string;
  name: string;
  released_at: string;
  set_type: string;
}

export interface NewCardData {
  oracle_id: string;
  name: string;
  type_line: string;
  mana_cost: string;
  cmc: number;
  oracle_text: string;
  set_code: string;
  set_name: string;
  rarity: string;
  keywords: string[];
  edhrec_rank: number | null;
  reprint?: boolean;
  in_deck?: boolean;
  /** A front-face land (backend `is_land_type_line`): the landbase's job, so it gets no "Make room". */
  land?: boolean;
}

export interface NewCardsResponse {
  sets: NewCardSetInfo[];
  cards: NewCardData[];
  total: number;
  color_identity: string[];
}

export type NewCardKind = 'beats_bar' | 'role_short';

export type ValueSource = 'vu' | 'quality' | 'combo' | 'prior';

export interface NewCardComponents {
  role_fit: number;
  /** The value slot, 0..1: Role Value (vu), Role Quality (quality), combo tier, or the price prior. */
  vu: number;
  value_source: ValueSource;
  synergy: number;
  plan_fit: number;
  plan_receipts?: PlanReceipts | null;
  synergy_presence: SynergyPresence;
  /** [context_tag, card_tag] pairs; only present when synergy_presence is 'estimated'. */
  synergy_receipts?: [string, string][];
}

export type EvalVerdict =
  | 'beats_bar' | 'role_short' | 'below_bar' | 'no_role_fit' | 'unscored' | 'land_admitted' | 'land_held'
  | 'not_found' | 'already_in_deck' | 'color_identity' | 'not_legal';

export type EvalMarginBasis = 'incumbent' | 'role_median';

/** One typed card's verdict from POST /build/evaluate-cards (the server computes every number). */
export interface CardEvaluation {
  requested: string;
  name: string | null;
  oracle_id: string | null;
  verdict: EvalVerdict;
  role: string | null;
  score: number | null;
  components: NewCardComponents | null;
  /** The bar incumbent's name; null for a role-median floor miss (the median names no card). */
  incumbent: string | null;
  incumbent_score: number | null;
  margin: number | null;
  margin_basis: EvalMarginBasis | null;
  replaces_damage: number | null;
  incumbent_adoption: number | null;
  incumbent_inclusion: number | null;
  land_reason: NewCardLandReason | null;
}

export interface EvaluateCardsResult {
  evaluations: CardEvaluation[];
  degraded: boolean;
  inputs_stale: boolean;
}

/** One judged pick: a new card the deck's owner may want, with the deck cards it competes with. Never a cut suggestion. */
export interface NewCardPick {
  card: string;
  oracle_id: string;
  set_code: string;
  /** The card's set has not released yet. */
  unreleased: boolean;
  /** A backend Role key (see ROLE_LABELS). */
  role: string;
  reason: string;
  /** The deck cards the judge named as the pick's competition in its role. */
  competes_with: string[];
  /** Other deck cards that fill the same role, strongest first. */
  role_incumbents: string[];
}

/** teaser: fits exist but the owner is not pro. no_fits: nothing on the shortlist. judged: 0-2 picks. */
export type NewCardsMode = 'judged' | 'teaser' | 'no_fits';

/** What the My Decks badge reads: a fresh What's New result trimmed to the picks' headline fields. */
export interface NewCardsSummary {
  mode: NewCardsMode;
  message: string | null;
  picks: Array<Pick<NewCardPick, 'card' | 'role' | 'reason' | 'unreleased' | 'set_code'>>;
  /** Names of the recent sets the result was computed against. */
  sets: string[];
}

export interface NewCardsResult {
  scoring_version: string;
  mode: NewCardsMode;
  picks: NewCardPick[];
  /** Backend-composed sentence shown in place of picks (teaser, no_fits, or judged with zero picks); null with picks. */
  message: string | null;
  shortlist_size: number;
  sets_analyzed: Array<{ code: string; name: string; released_at: string }>;
  cards_available: number;
  sets_snapshot: string[];
  /** Set rotation moved on since this analysis ran (served by the backend). */
  stale?: boolean;
  color_identity: string[];
}

export interface DiffData {
  has_changes: boolean;
  counts: {
    added: number;
    removed: number;
    changed: number;
    unchanged: number;
  };
  added: { qty: number; card_name: string }[];
  removed: { qty: number; card_name: string }[];
  changed: { card_name: string; old_qty: number; new_qty: number }[];
  /** ISO timestamp of the snapshot used as baseline (when diffing vs source) */
  base_snapshot_date?: string;
  /** ISO timestamp when the source was last fetched */
  fetched_at?: string;
  source_snapshot_id?: number;
}

export interface RevisionStats {
  version: number;
  nonland_count: number;
  avg_mv: number;
  total_pips: number;
  pip_counts: Record<string, number>;
  avg_vu: number;
  vu_confidence: { exact: number; classified: number; heuristic: number; none: number };
  computed_at: string;
}

export interface Revision {
  id: number;
  build_id: string;
  label: string | null;
  snapshot_type: string;
  source_type?: string | null;
  metadata: Record<string, unknown>;
  created_at?: string;
  hash: string | null;
  stats: RevisionStats | null;
  bracket: number | null;
  traditional_scale: number | null;
  delta: { added: number; removed: number; changed: number } | null;
  overlay_summary?: { cuts: number; overrides: number };
}

export interface CardDelta { name: string; qty: number }
export interface SwapPair { out: string | null; in: string; source: string | null }
/** One change: consecutive revisions grouped under one note. revision_ids is oldest first; [0] keys every write. */
export interface DeckChange {
  revision_ids: number[];
  folded_ids: number[];
  note: string | null;            // null = never answered, '' = skipped
  title: string;
  created_at: string;
  ins: CardDelta[];
  outs: CardDelta[];
  syncs: number;
  stats_before: RevisionStats | null;
  stats_after: RevisionStats | null;
  bracket_before: number | null;
  bracket_after: number | null;
  swap: SwapPair | null;
  label: string;
}

export interface DeckContext {
  meta_format?: string;
  play_style?: string;
  budget?: string;
  notes?: string;
  [key: string]: unknown;
}

export interface StatsData {
  total_decks: number;
  total_users: number;
  total_analyses: number;
  avg_power?: number;
  [key: string]: unknown;
}

export interface HealthData {
  status: string;
  db_connected: boolean;
  version?: string;
}

export interface AuthConfig {
  auth_enabled: boolean;
  invite_only: boolean;
  oauth_providers: string[];
}

// ─── Analysis Result Types ───────────────────────────────────────────────────

export interface WinTurnEstimate {
  display: string;
  /** WotC's earliest expected win for the shown bracket; null at B5 ("any turn"). */
  expected_turn?: number | null;
  earliest?: number;
  average?: number;
  start?: number;
  end?: number;
  category?: string;
}

export interface ComboAnalysis {
  total_combos: number;
  /** Logical combos (one per engine) Commander Spellbook rates for Bracket 4+ tables. */
  high_bracket_combo_count?: number;
  /** False when this analysis predates per-combo storage: total_combos is a real count but
   *  the detail lists (combo_details, missing_pieces, ...) are empty rather than rebuilt. */
  details_available?: boolean;
  [key: string]: unknown;
}

export interface TemplateWrite {
  themes?: string[];
  slot_overrides: Record<string, number>;
  /** theme slug -> target cards; omit to leave the stored targets alone, `{}` clears them. */
  theme_targets?: Record<string, number>;
}

/** GET /decks/{id}/quick-stats. `themes` is the deck's themes (the builder's chosen themes once
 *  seeded, else discovery) — what the banner chips render; `synergy_analysis` is the analysis itself. */
export interface QuickStatsPayload {
  synergy_analysis: SynergyAnalysis | null;
  combo_analysis: ComboAnalysis | null;
  themes: SynergyTheme[];
  /** What the deck's own cards read as (backend-computed discovery), beside the chosen `themes`. */
  reads_as?: SynergyTheme[];
}

/** GET /decks/{id}/favorites: the build's starred names on its list; `inherited` = starred on the base deck and
 *  not on this build (empty on 'base') — this build cannot unstar those. */
export interface FavoritesPayload {
  favorites: string[];
  inherited: string[];
}

export interface SynergyTheme {
  slug: string;
  value: string;
  count?: number;
  /** Reads-as only: members as a share of the deck's nonland cards (0-1) and their lift over the colour identity's pool. */
  share?: number;
  lift?: number;
  /** present when theme was added by mechanical analysis rather than EDHREC */
  source?: 'mechanical';
}

export interface SynergyAnalysis {
  available?: boolean;
  score?: number;
  deck_synergy_score?: number;
  themes?: SynergyTheme[];
  synergy_pairs?: Array<{ cards: string[]; description?: string; score?: number }>;
  commander_salt?: number;
  deck_salt?: number;
  deck_salt_card_count?: number;
  [key: string]: unknown;
}

export interface ManaSources {
  /** Pure lands (front-face is a land). */
  lands?: number;
  /** MDFC land-backs (front-face non-land, back-face land). */
  mdfc_lands?: number;
  /** lands + mdfc_lands — what the land-floor check compares against. */
  total_land_sources?: number;
  rocks?: number;
  dorks?: number;
  total?: number;
}

export interface VUProfile {
  draw_vu: number;
  ramp_vu: number;
  is_repeating: boolean;
  ramp_is_land: boolean;
  mana_tokens: number;
  mana_burst_net: number;
  draw_is_opponent_triggered: boolean;
  looting: boolean;
  confidence: string;
  /** Mana per activation of a repeatable non-tap source (backend `CardValue.repeatable_mana`); data contract only. */
  repeatable_mana: number;
}

/** The owner's bracket declaration (backend enum `BracketDeclaration`). */
export type BracketDeclarationKind = 'exhibition' | 'cedh';

/** What the owner declared and whether it applies (backend-computed; the frontend only renders it). */
export interface BracketDeclarationOutcome {
  declaration: BracketDeclarationKind;
  /** The declared bracket: 1 (Exhibition) or 5 (cEDH). */
  bracket: number;
  /** False: a declared Exhibition deck breaks a B1 rule (`blocking`), so it shows its rule floor. */
  applied: boolean;
  text: string;
  blocking: FloorReason[];
}

/** One "plays like" annotation (backend `PlaysLikeReason`); it never moves the shown bracket. */
export interface PlaysLikeReason {
  basis: 'cedh_build' | 'combo_turn' | 'board_turn';
  /** The bracket the evidence reads as. */
  bracket: number;
  text: string;
  /** The turn the reason states (rounded half up, never before T2), for the turn-based reasons. */
  turn: number | null;
  /** The shown bracket's earliest expected win (WotC), for the turn-based reasons. */
  expected_turn: number | null;
  cards: string[];
  combos: FloorReasonCombo[];
}

/** Where the deck sits within its shown bracket, from the unrounded power score. */
export type BracketPosition = 'low' | 'mid' | 'high';

/** A combo cited as evidence for a floor reason: its pieces by name and Spellbook's rating. */
export interface FloorReasonCombo {
  id: string;
  cards: string[];
  /** Commander Spellbook's bracket rating (null = unrated). */
  rating: number | null;
}

/** One WotC hard rule's verdict on the deck (backend-computed; the frontend only renders it). */
export interface FloorReason {
  rule: 'game_changers' | 'mass_land_denial' | 'extra_turns' | 'two_card_combos';
  /** The bracket this reason holds the deck at (the floor is the highest). */
  bracket: number;
  text: string;
  cards: string[];
  combos: FloorReasonCombo[];
}

/** Combos Commander Spellbook rates for Bracket 4+ tables, when that is a warning. */
/** A deck-read callout against the bracket benchmark (backend `bracket_fit.Callout`): one more reason line. */
export interface BracketReadCallout {
  kind: 'tutors_like';
  /** The bracket the deck reads like. */
  bracket: number;
  text: string;
}

export interface BracketComboWarning {
  count: number;
  /** The bracket those combos are rated for (4). */
  bracket: number;
  /** True when the deck is at B4+ only because of these combos. */
  drove_bracket: boolean;
  combos: string[][];
  practical_warning: string;
}

/** The bracket fields every bracket read carries (the backend's one derivation). */
export type BracketFieldKey = 'wotc_bracket' | 'minimum_bracket' | 'floor_reasons' | 'plays_like_reasons'
  | 'bracket_position' | 'bracket_declaration';

/** A build's bracket fields (variants.getPower and the builds listing); null while pending. */
export interface BuildBracketFields {
  wotc_bracket: number | null;
  minimum_bracket: number | null;
  floor_reasons: FloorReason[] | null;
  plays_like_reasons: PlaysLikeReason[] | null;
  bracket_position: BracketPosition | null;
  bracket_declaration: BracketDeclarationOutcome | null;
}

export interface PowerLevelResult {
  overall_score?: number;
  /** The shown bracket: the rule floor, or the owner's declaration where it applies. */
  wotc_bracket?: number;
  /** The floor: the highest bracket among `floor_reasons` (B2–B4). */
  minimum_bracket?: number;
  floor_reasons?: FloorReason[];
  /** Annotations that never move the shown bracket ("built like cEDH", an early combo). */
  plays_like_reasons?: PlaysLikeReason[];
  /** null: no unrounded score (an older analysis) or no cut points for the bracket (B1). */
  bracket_position?: BracketPosition | null;
  bracket_declaration?: BracketDeclarationOutcome | null;
  bracket_combo_warning?: BracketComboWarning | null;
  /** Tutor callouts against the bracket benchmark; rendered beside the plays-like reasons. */
  bracket_read_callouts?: BracketReadCallout[];
  /** The 1–10 power estimate, rounded. It places nothing. */
  traditional_scale?: number;
  /** The unrounded power estimate (analyses since the plays-like frame). */
  power_score_raw?: number;
  confidence?: number;
  speed_category?: string;
  estimated_win_turn?: WinTurnEstimate | string;
  win_turn_estimate?: WinTurnEstimate;
  combo_analysis?: ComboAnalysis;
  synergy_analysis?: SynergyAnalysis;
  power_indicators?: Record<string, string[]>;
  indicators?: Record<string, number | { score: number; label?: string; reasoning?: string }>;
  mana_sources?: ManaSources;
  mana_curve?: Record<string, number>;
  type_breakdown?: Record<string, number>;
  summary?: string;
  assessment?: string;
  narrative?: string;
  reasoning?: string;
  analysis_text?: string;
  avg_cmc?: number;
  /** Commander's mana value — used by value calculations framework */
  commander_mv?: number | null;
  /** Per-card archetype VU data — present when IR walker has compiled rules */
  vu_profile?: Record<string, VUProfile>;
  /** Engines Online, computed server-side (assemble_view); online_turn null = beyond T7 */
  value_gen?: { online_turn: number | null; cumulative_surplus: number; source: 'backend' };
  [key: string]: unknown;
}

export interface Swap {
  card_out: string;
  card_in: string;
  reason?: string;
}

export interface SwapPlanResultData {
  plan_name?: string;
  created_at?: string;
  swaps?: Swap[];
  total_swaps?: number;
  section?: string;
}

export interface SwapPlan {
  result_data?: SwapPlanResultData;
}

/** One actionable swap from the structured optimization response. */
export interface OptimizationSuggestion {
  card_out: string;
  card_in: string;
  reason: string;
}

/** One prose section from the structured optimization response. */
export interface OptimizationSection {
  title: string;
  body: string;
}

/** Structured optimization result returned by the LLM when JSON parsing succeeds. */
export interface StructuredImprovements {
  sections: OptimizationSection[];
  suggestions: OptimizationSuggestion[];
}

/**
 * Parse the `improvements` field of an OptimizationResult.
 *
 * New format: the field is a JSON object (already deserialized by the API) with
 * `sections` and `suggestions` keys.
 * Legacy format: a plain markdown string — returned as a single-section object
 * with an empty suggestions list.
 */
export function parseImprovements(
  improvements: unknown,
): StructuredImprovements | null {
  if (!improvements) return null;

  // Already a structured object (new format from the API)
  if (typeof improvements === 'object' && improvements !== null) {
    const obj = improvements as Record<string, unknown>;
    if (Array.isArray(obj.sections) && Array.isArray(obj.suggestions)) {
      return improvements as StructuredImprovements;
    }
  }

  // Legacy: plain string — wrap it
  if (typeof improvements === 'string' && improvements.trim().length > 0) {
    // Try JSON parse in case it was double-serialized as a string
    if (improvements.trim().startsWith('{')) {
      try {
        const parsed = JSON.parse(improvements) as Record<string, unknown>;
        if (Array.isArray(parsed.sections) && Array.isArray(parsed.suggestions)) {
          return parsed as unknown as StructuredImprovements;
        }
      } catch {
        // not valid JSON — fall through to plain text treatment
      }
    }
    return {
      sections: [{ title: 'Suggestions', body: improvements }],
      suggestions: [],
    };
  }

  return null;
}

export interface OptimizationResult {
  swap_plans?: SwapPlan[];
  /** May be a StructuredImprovements object (new) or a plain markdown string (legacy). */
  improvements?: StructuredImprovements | string;
  analysis_text?: string;
  [key: string]: unknown;
}

export interface PrimerResult {
  primer_text?: string;
  generated_at?: string;
  [key: string]: unknown;
}

export interface AnalysisTaskResult {
  power_level?: PowerLevelResult;
  optimization?: OptimizationResult;
  primer?: PrimerResult;
  new_cards?: NewCardsResult;
  [key: string]: unknown;
}

// ─── Auth ─────────────────────────────────────────────────────────────────────

export const auth = {
  async login(email: string, password: string): Promise<User> {
    const r = await apiFetch('/api/v1/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    return ok<User>(r, 'Login failed');
  },

  async register(email: string, password: string, displayName: string, inviteToken?: string): Promise<User> {
    const body: Record<string, string> = { email, password, display_name: displayName };
    if (inviteToken) body.invite_token = inviteToken;
    const r = await apiFetch('/api/v1/auth/register', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    return ok<User>(r, 'Registration failed');
  },

  async logout(): Promise<void> {
    await apiFetch('/api/v1/auth/logout', { method: 'POST' });
  },

  async me(): Promise<User | null> {
    const r = await apiFetch('/api/v1/auth/me');
    if (!r.ok) return null;
    return r.json() as Promise<User>;
  },

  async getConfig(): Promise<AuthConfig> {
    const r = await apiFetch('/api/v1/auth/config');
    return ok<AuthConfig>(r, 'Failed to get auth config');
  },
};

// ─── Decks ────────────────────────────────────────────────────────────────────

export const decks = {
  async list(sort: 'updated_at' | 'win_rate' = 'updated_at'): Promise<Deck[]> {
    const r = await apiFetch(`/api/v1/decks/list?sort=${sort}`);
    const data = await ok<{ decks: Deck[]; count: number }>(r, 'Failed to list decks');
    return data.decks;
  },

  async listMy(): Promise<Deck[]> {
    const r = await apiFetch('/api/v1/decks/my');
    const data = await ok<{ decks: Deck[]; count: number }>(r, 'Failed to list your decks');
    return data.decks;
  },

  async get(deckId: string): Promise<Deck> {
    const r = await apiFetch(`/api/v1/decks/${deckId}`);
    return ok<Deck>(r, 'Deck not found');
  },

  async delete(deckId: string): Promise<void> {
    const r = await apiFetch(`/api/v1/decks/${deckId}`, { method: 'DELETE' });
    if (!r.ok) return apiError(r, 'Failed to delete deck');
  },

  async update(deckId: string, decklist: string, speedCategory?: string, commanders?: string[]): Promise<Deck> {
    const body: Record<string, string | string[]> = { decklist };
    if (speedCategory) body.speed_category = speedCategory;
    if (commanders !== undefined) body.commanders = commanders;
    const r = await apiFetch(`/api/v1/decks/${deckId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    return ok<Deck>(r, 'Failed to update deck');
  },

  /** The owner's (or an admin's) bracket declaration; null clears it. */
  async setBracketDeclaration(deckId: string, declaration: BracketDeclarationKind | null): Promise<void> {
    const r = await apiFetch(`/api/v1/decks/${deckId}/bracket-declaration`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ declaration }),
    });
    await ok(r, 'Failed to update the bracket declaration');
  },

  async updateVisibility(deckId: string, visibility: 'public' | 'private'): Promise<Deck> {
    const r = await apiFetch(`/api/v1/decks/${deckId}/visibility`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ visibility }),
    });
    return ok<Deck>(r, 'Failed to update visibility');
  },

  async setHistoryInPrimer(deckId: string, enabled: boolean): Promise<{ history_in_primer: boolean }> {
    const r = await apiFetch(`/api/v1/decks/${deckId}/history-in-primer`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ enabled }),
    });
    return ok<{ history_in_primer: boolean }>(r, 'Failed to update the primer history setting');
  },

  async getCardMetadata(deckId: string): Promise<{ card_metadata: Record<string, CardMeta> }> {
    const r = await apiFetch(`/api/v1/decks/${deckId}/card-metadata`);
    return ok<{ card_metadata: Record<string, CardMeta> }>(r, 'Failed to get card metadata');
  },

  async getCardMetadataByNames(names: string[]): Promise<Record<string, CardMeta>> {
    const r = await apiFetch('/api/v1/decks/card-metadata-bulk', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ names }),
    });
    const data = await ok<{ card_metadata: Record<string, CardMeta> }>(r, 'Failed to get card metadata');
    return data.card_metadata;
  },

  async getTagGroups(deckId: string): Promise<TagGroup[]> {
    const r = await apiFetch(`/api/v1/decks/${deckId}/tag-groups`);
    return (await ok<{ groups: TagGroup[] }>(r, 'Failed to get tag groups')).groups;
  },

  async getTagGroupsByNames(names: string[]): Promise<TagGroup[]> {
    const r = await apiFetch('/api/v1/decks/tag-groups-bulk', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ names }),
    });
    return (await ok<{ groups: TagGroup[] }>(r, 'Failed to get tag groups')).groups;
  },

  async getQuickStats(deckId: string): Promise<QuickStatsPayload> {
    const r = await apiFetch(`/api/v1/decks/${deckId}/quick-stats`);
    return ok<QuickStatsPayload>(r, 'Failed to get quick stats');
  },

  async generateDeckRoast(deckId: string, force = false): Promise<{ roast: string; deck_name: string; cached: boolean }> {
    const url = `/api/v1/decks/${deckId}/roast${force ? '?force=true' : ''}`;
    const r = await apiFetch(url, { method: 'POST' });
    return ok<{ roast: string; deck_name: string; cached: boolean }>(r, 'Failed to generate deck roast');
  },

  async importUrl(url: string): Promise<Deck & { analysis_started?: boolean; credits_warning?: string; upgrade_url?: string }> {
    const r = await apiFetch('/api/v1/decks/import-url', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ url }),
    });
    return ok(r, 'Failed to import deck');
  },

  async addManual(name: string, format: string, decklist: string, commander?: string, sourceType?: string): Promise<Deck> {
    const r = await apiFetch('/api/v1/decks/manual', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, format, commander: commander ?? null, decklist, source_type: sourceType ?? 'manual' }),
    });
    return ok<Deck>(r, 'Failed to add deck');
  },

  async bulkDiscover(platform: string, username: string): Promise<{ decks: DiscoveredDeck[] }> {
    const r = await apiFetch(`/api/v1/decks/bulk-discover?platform=${encodeURIComponent(platform)}&username=${encodeURIComponent(username)}`);
    return ok<{ decks: DiscoveredDeck[] }>(r, 'Failed to discover decks');
  },

  async getContext(deckId: string): Promise<{ deck_id: string; context: DeckContext | null }> {
    const r = await apiFetch(`/api/v1/decks/${deckId}/context`);
    return ok<{ deck_id: string; context: DeckContext | null }>(r, 'Failed to get deck context');
  },

  async updateContext(deckId: string, context: DeckContext): Promise<{ deck_id: string; context: DeckContext }> {
    const r = await apiFetch(`/api/v1/decks/${deckId}/context`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(context),
    });
    return ok<{ deck_id: string; context: DeckContext }>(r, 'Failed to update deck context');
  },

  async clearContext(deckId: string): Promise<{ status: string }> {
    const r = await apiFetch(`/api/v1/decks/${deckId}/context`, { method: 'DELETE' });
    return ok<{ status: string }>(r, 'Failed to clear deck context');
  },

  async getSwapPlans(deckId: string): Promise<{ swap_plans: SwapPlan[]; count: number }> {
    const r = await apiFetch(`/api/v1/decks/${deckId}/swap-plans`);
    return ok<{ swap_plans: SwapPlan[]; count: number }>(r, 'Failed to get swap plans');
  },

  async getRevisions(deckId: string, buildId: string): Promise<Revision[]> {
    const r = await apiFetch(`/api/v1/decks/${deckId}/revisions?build_id=${encodeURIComponent(buildId)}`);
    const data = await ok<{ revisions: Revision[] }>(r, 'Failed to load revisions');
    return data.revisions;
  },

  async getChanges(deckId: string, buildId: string): Promise<DeckChange[]> {
    const r = await apiFetch(`/api/v1/decks/${deckId}/changes?build_id=${encodeURIComponent(buildId)}`);
    const data = await ok<{ changes: DeckChange[] }>(r, 'Failed to load changes');
    return data.changes;
  },

  async setChangeNote(deckId: string, buildId: string, revisionId: number, note: string): Promise<{ revision_id: number }> {
    const r = await apiFetch(`/api/v1/decks/${deckId}/changes/${revisionId}/note?build_id=${encodeURIComponent(buildId)}`, jsonBody('PUT', { note }));
    return ok<{ revision_id: number }>(r, 'Failed to save note');
  },

  async joinPrevious(deckId: string, buildId: string, revisionId: number, note: string): Promise<{ revision_id: number }> {
    const r = await apiFetch(`/api/v1/decks/${deckId}/changes/${revisionId}/join-previous?build_id=${encodeURIComponent(buildId)}`, jsonBody('POST', { note }));
    return ok<{ revision_id: number }>(r, 'Failed to join the change');
  },

  async splitChange(deckId: string, buildId: string, revisionId: number): Promise<{ revision_id: number }> {
    const r = await apiFetch(`/api/v1/decks/${deckId}/changes/${revisionId}/split?build_id=${encodeURIComponent(buildId)}`, jsonBody('POST'));
    return ok<{ revision_id: number }>(r, 'Failed to split the change');
  },

  async revertChange(deckId: string, buildId: string, revisionId: number): Promise<{ revision_id: number }> {
    const r = await apiFetch(`/api/v1/decks/${deckId}/changes/${revisionId}/revert?build_id=${encodeURIComponent(buildId)}`, jsonBody('POST'));
    return ok<{ revision_id: number }>(r, 'Failed to revert the change');
  },

  async createSnapshot(deckId: string, label: string, metadata?: Record<string, unknown>): Promise<{ snapshot_id?: number; deduplicated: boolean }> {
    const r = await apiFetch(`/api/v1/decks/${deckId}/snapshots`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ label, metadata }),
    });
    return ok<{ snapshot_id?: number; deduplicated: boolean }>(r, 'Failed to create snapshot');
  },

  async diffVsSource(deckId: string): Promise<DiffData> {
    const r = await apiFetch(`/api/v1/decks/${deckId}/diff/source`, { method: 'POST' });
    return ok<DiffData>(r, 'Failed to diff vs source');
  },

  async getLatestSourceDiff(deckId: string): Promise<DiffData | null> {
    const r = await apiFetch(`/api/v1/decks/${deckId}/diff/source/latest`);
    if (!r.ok) return null;
    const body = await r.json() as { diff: null; message: string } | DiffData;
    if ('diff' in body && body.diff === null) return null;
    return body as DiffData;
  },

  async applySourceVersion(deckId: string): Promise<ApplySourceResult> {
    const r = await apiFetch(`/api/v1/decks/${deckId}/apply/source`, { method: 'POST' });
    return ok<ApplySourceResult>(r, 'Failed to apply Moxfield version');
  },

  async diffBetweenSnapshots(deckId: string, s1: string, s2: string): Promise<DiffData> {
    const r = await apiFetch(`/api/v1/decks/${deckId}/diff/${encodeURIComponent(s1)}/${encodeURIComponent(s2)}`);
    return ok<DiffData>(r, 'Failed to diff between snapshots');
  },

  async getNewCards(deckId: string): Promise<NewCardsResponse> {
    const r = await apiFetch(`/api/v1/decks/${deckId}/new-cards`);
    return ok<NewCardsResponse>(r, 'Failed to get new cards');
  },

  async updateBannerPosition(deckId: string, pos: { x: number; y: number }): Promise<void> {
    const r = await apiFetch(`/api/v1/decks/${deckId}/banner-position`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(pos),
    });
    await ok(r, 'Failed to update banner position');
  },

  async rename(deckId: string, name: string): Promise<{ deck_id: string; name: string }> {
    const r = await apiFetch(`/api/v1/decks/${deckId}/name`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name }),
    });
    return ok<{ deck_id: string; name: string }>(r, 'Failed to rename deck');
  },

  /** Funny name ideas for the title editor (owner only). Cached per card list; `force` rerolls. */
  async nameIdeas(deckId: string, force = false): Promise<DeckNameIdeas> {
    const r = await apiFetch(`/api/v1/decks/${deckId}/name-ideas${force ? '?force=true' : ''}`, { method: 'POST' });
    return ok<DeckNameIdeas>(r, "Couldn't come up with names");
  },

  async exportMoxfield(deckId: string): Promise<string> {
    const r = await apiFetch(`/api/v1/decks/${deckId}/export?format=moxfield`);
    if (!r.ok) return apiError(r, `Export failed: ${r.status}`);
    return r.text();
  },

  async getTags(deckId: string): Promise<Record<string, string[]>> {
    const r = await apiFetch(`/api/v1/decks/${deckId}/tags`);
    return ok<Record<string, string[]>>(r, 'Failed to get tags');
  },

  /** Add `addCard` to a build, cutting `removeCard` (null: an add into free room). Returns the build's payload. */
  async swapCard(
    deckId: string,
    addCard: string,
    removeCard: string | null,
    opts: { buildId: string; source: SwapSource },
  ): Promise<BuildPayload> {
    const r = await apiFetch(`/api/v1/decks/${deckId}/swap-card`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ add_card: addCard, remove_card: removeCard, build_id: opts.buildId, source: opts.source }),
    });
    return ok<BuildPayload>(r, 'Failed to swap card');
  },

  async addTag(deckId: string, cardName: string, tag: string, buildId = 'base'): Promise<void> {
    const r = await apiFetch(`/api/v1/decks/${deckId}/tags`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ card_name: cardName, tag, build_id: buildId }),
    });
    await ok(r, 'Failed to add tag');
  },

  async removeTag(deckId: string, cardName: string, tag: string, buildId = 'base'): Promise<void> {
    const params = new URLSearchParams({ card_name: cardName, tag, build_id: buildId });
    const r = await apiFetch(`/api/v1/decks/${deckId}/tags?${params}`, { method: 'DELETE' });
    if (!r.ok) return apiError(r, 'Failed to remove tag');
  },

  async favorites(deckId: string, buildId = 'base'): Promise<FavoritesPayload> {
    const params = new URLSearchParams({ build_id: buildId });
    const r = await apiFetch(`/api/v1/decks/${deckId}/favorites?${params}`);
    return ok<FavoritesPayload>(r, 'Failed to load favorites');
  },

  async addFavorite(deckId: string, cardName: string, buildId = 'base'): Promise<void> {
    const r = await apiFetch(`/api/v1/decks/${deckId}/favorites`, jsonBody('POST', { card_name: cardName, build_id: buildId }));
    await ok(r, 'Failed to favorite card');
  },

  async removeFavorite(deckId: string, cardName: string, buildId = 'base'): Promise<void> {
    const params = new URLSearchParams({ card_name: cardName, build_id: buildId });
    const r = await apiFetch(`/api/v1/decks/${deckId}/favorites?${params}`, { method: 'DELETE' });
    if (!r.ok) return apiError(r, 'Failed to unfavorite card');
  },

  async renameTag(deckId: string, oldTag: string, newTag: string, buildId = 'base'): Promise<void> {
    const r = await apiFetch(`/api/v1/decks/${deckId}/tags/rename`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ old_tag: oldTag, new_tag: newTag, build_id: buildId }),
    });
    await ok(r, 'Failed to rename tag');
  },

  async getGameStats(deckId: string): Promise<{ wins: number; losses: number; draws: number; total: number }> {
    const r = await apiFetch(`/api/v1/decks/${deckId}/game-stats`);
    return ok<{ wins: number; losses: number; draws: number; total: number }>(r, 'Failed to get game stats');
  },
};

// ─── Tasks ────────────────────────────────────────────────────────────────────

export const tasks = {
  // Deck-scoped routes always run on the analysis lane — a system task has no deck_id and is
  // never returned here.
  async startAnalysis(deckId: string, forceNew = false): Promise<AnalysisTaskData> {
    const r = await apiFetch(`/api/v1/tasks/analyze/${deckId}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ force_new: forceNew }),
    });
    return ok<AnalysisTaskData>(r, 'Failed to start analysis task');
  },

  async getDeckTask(deckId: string): Promise<{ task: AnalysisTaskData | null }> {
    const r = await apiFetch(`/api/v1/tasks/deck/${deckId}`);
    return ok<{ task: AnalysisTaskData | null }>(r, 'Failed to get deck task');
  },

  async getTask(taskId: string): Promise<TaskData> {
    const r = await apiFetch(`/api/v1/tasks/${taskId}`);
    return ok<TaskData>(r, 'Task not found');
  },

  async startTuning(deckId: string, params: TuneParams): Promise<AnalysisTaskData> {
    const r = await apiFetch(`/api/v1/tasks/tune/${deckId}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(params),
    });
    return ok<AnalysisTaskData>(r, 'Failed to start tuning task');
  },

  async startNewCards(deckId: string): Promise<AnalysisTaskData> {
    const r = await apiFetch(`/api/v1/tasks/new-cards/${deckId}`, { method: 'POST' });
    return ok<AnalysisTaskData>(r, 'Failed to start new cards analysis');
  },
};

// ─── Results ──────────────────────────────────────────────────────────────────

export const results = {
  async list(limit = 20): Promise<AnalysisResult[]> {
    const r = await apiFetch(`/api/v1/results/list?limit=${limit}`);
    const data = await ok<{ analyses: AnalysisResult[] }>(r, 'Failed to list results');
    return data.analyses;
  },
};

// ─── Chat ─────────────────────────────────────────────────────────────────────

export const chat = {
  async start(deckId: string, section: string, analysisContext: Record<string, unknown> | null): Promise<{ session_token: string }> {
    const r = await apiFetch('/api/v1/chat/ephemeral/start', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ deck_id: deckId, section, analysis_context: analysisContext ?? {} }),
    });
    return ok<{ session_token: string }>(r, 'Failed to start chat');
  },

  async sendStream(sessionToken: string, message: string, deckId: string): Promise<Response> {
    return apiFetch('/api/v1/chat/ephemeral/message-stream', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ session_token: sessionToken, message, deck_id: deckId }),
    });
  },

  async close(sessionToken: string): Promise<{ status: string }> {
    const r = await apiFetch('/api/v1/chat/ephemeral/close', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ session_token: sessionToken }),
    });
    return ok<{ status: string }>(r, 'Failed to close chat');
  },

  async executeAction(sessionToken: string, action: string, section: string, params: Record<string, unknown>): Promise<{ status: string; result?: unknown }> {
    const r = await apiFetch('/api/v1/chat/ephemeral/execute_action', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ session_token: sessionToken, action, section, params }),
    });
    return ok<{ status: string; result?: unknown }>(r, 'Failed to execute action');
  },
};

// ─── Users ────────────────────────────────────────────────────────────────────

export const users = {
  async leaderboard(limit = 50): Promise<{ leaderboard: LeaderboardEntry[] }> {
    const r = await apiFetch(`/api/v1/users/leaderboard?limit=${limit}`);
    return ok(r, 'Failed to fetch leaderboard');
  },

  async getProfile(userId: string): Promise<ProfileData> {
    const r = await apiFetch(`/api/v1/users/${userId}/profile`);
    return ok<ProfileData>(r, 'User not found');
  },

  async updateDisplayName(displayName: string): Promise<User> {
    const r = await apiFetch('/api/v1/users/me/display-name', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ display_name: displayName }),
    });
    return ok<User>(r, 'Failed to update display name');
  },

  async generateRoast(userId: string, force = false): Promise<{ roast: string; cached: boolean; published: boolean; generated_at?: string }> {
    const url = force ? `/api/v1/users/${userId}/roast?force=true` : `/api/v1/users/${userId}/roast`;
    const r = await apiFetch(url, { method: 'POST' });
    return ok(r, 'Failed to generate roast');
  },

  async toggleRoastPublish(userId: string): Promise<{ published: boolean }> {
    const r = await apiFetch(`/api/v1/users/${userId}/roast/publish`, { method: 'PATCH' });
    return ok(r, 'Failed to update roast visibility');
  },

  async findCardHome(cardName: string): Promise<FindCardHomeResult> {
    const r = await apiFetch('/api/v1/users/me/find-card-home', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ card_name: cardName }),
    });
    return ok<FindCardHomeResult>(r, 'Failed to find card home');
  },

  async decaSketchConcepts(commander: string, theme: string, bracket: number): Promise<DecASketchConceptsResult> {
    const r = await apiFetch('/api/v1/users/me/dec-a-sketch/concepts', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ commander, theme, bracket }),
    });
    return ok<DecASketchConceptsResult>(r, 'Failed to generate concepts');
  },

  async decaSketchBuild(commander: string, strategy: string, bracket: number, envelope: DecASketchEnvelope, seedCards: string[], fillThemes: string[]): Promise<DecASketchBuildStarted> {
    const r = await apiFetch('/api/v1/users/me/dec-a-sketch/build', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ commander, strategy, bracket, seed_cards: seedCards, fill_themes: fillThemes, ...envelope }),
    });
    return ok<DecASketchBuildStarted>(r, 'Failed to start build');
  },

  async decaSketchGetRun(runId: number): Promise<DasRun> {
    const r = await apiFetch(`/api/v1/users/me/dec-a-sketch/runs/${runId}`);
    return ok<DasRun>(r, 'Failed to fetch run');
  },

  async updateDasRunOutcome(runId: number, outcome: 'saved' | 'rejected' | 'pending', savedDeckId?: string): Promise<void> {
    const r = await apiFetch(`/api/v1/users/me/dec-a-sketch/runs/${runId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ outcome, saved_deck_id: savedDeckId ?? null }),
    });
    await ok<unknown>(r, 'Failed to update the sketch run');
  },

  async uploadAvatar(file: File): Promise<{ avatar_url: string }> {
    const fd = new FormData();
    fd.append('file', file);
    const r = await apiFetch('/api/v1/users/me/avatar', { method: 'POST', body: fd });
    return ok<{ avatar_url: string }>(r, 'Failed to upload avatar');
  },
};

// ─── Billing ──────────────────────────────────────────────────────────────────

export const billing = {
  async getStatus(): Promise<BillingStatus> {
    const r = await apiFetch('/api/v1/billing/status');
    return ok<BillingStatus>(r, 'Failed to get billing status');
  },

  async createCheckout(): Promise<{ url: string }> {
    const r = await apiFetch('/api/v1/billing/create-checkout', { method: 'POST' });
    return ok<{ url: string }>(r, 'Failed to create checkout');
  },

  async getPortal(): Promise<{ url: string }> {
    const r = await apiFetch('/api/v1/billing/portal', { method: 'POST' });
    return ok<{ url: string }>(r, 'Failed to open portal');
  },
};

// ─── Admin ────────────────────────────────────────────────────────────────────

/** `GET /api/v1/admin/overview` — mirrors `metacommand/web/overview.py`'s `OverviewStats`
 * (spec docs/superpowers/specs/2026-09-22-admin-overview-stats-design.md §3). Every timestamp
 * is an ISO string or null; `decks.by_source` keys are `decks.source_type` values as stored. */
export interface CardStats {
  oracle_cards: number;
  printings: number;
  classified: number;
  rulings: number;
  last_import_at: string | null;
  is_stale: boolean;
  rulings_imported_at: string | null;
  rules_imported_at: string | null;
}

export interface DeckStats {
  total: number;
  by_source: Record<string, number>;
  variants: number;
  public: number;
  private: number;
  analysed_24h: number;
}

export interface UserStats {
  total: number;
  new_30d: number;
  active_30d: number;
  admins: number;
}

export interface TagStats {
  otag_tags: number;
  otag_rows: number;
  hierarchy_edges: number;
  containment_edges: number;
  tagger_file_at: string | null;
}

export interface EdhrecStats {
  commander_pages: number;
  themes: number;
}

export interface WorkStats {
  analyses_total: number;
  llm_cost_today_usd: number;
  llm_cost_7d_usd: number;
  classified_today: number;
  classify_daily_cap: number;
  active_system_tasks: number;
}

export interface OverviewStats {
  generated_at: string;
  cards: CardStats;
  decks: DeckStats;
  users: UserStats;
  tags: TagStats;
  edhrec: EdhrecStats;
  work: WorkStats;
}

/** The admin exception log (`metacommand/web/error_log.py`, spec
 * docs/superpowers/specs/2026-10-12-admin-exception-log-design.md §5). Timestamps are the
 * database's `'YYYY-MM-DD HH:MM:SS'` UTC text. Messages and tracebacks can hold user data:
 * they come only from these admin routes. */
/** `'unknown'`: a row whose stored kind this backend build no longer knows (it reads it so,
 * rather than failing the tab). */
export type ErrorKindValue = 'llm_provider' | 'internal' | 'unknown';

/** `GET /admin/errors`: one failure group (fingerprint) in the window; `count` sums rollups. */
export interface ErrorGroup {
  fingerprint: string;
  kind: ErrorKindValue;
  exc_type: string;
  count: number;
  first_seen: string;
  last_seen: string;
  latest_message: string;
  latest_ref: string;
  recent_refs: string[];
}

/** One `error_events` row. `occurrences` > 1 is a rollup that absorbed `extra_ref_count` more refs. */
export interface ErrorOccurrence {
  id: number;
  ref: string;
  fingerprint: string;
  kind: ErrorKindValue;
  exc_type: string;
  created_at: string;
  method: string | null;
  path: string | null;
  user_id: string | null;
  user_name: string | null;
  context: string | null;
  occurrences: number;
  extra_ref_count: number;
  message: string;
  traceback: string;
}

/** `GET /admin/errors/ref/{ref}`: `ref` is the backend's normalised form of what was pasted;
 * `absorbed` = found only inside a rollup's kept refs. */
export interface ErrorRefSearch {
  ref: string;
  absorbed: boolean;
  matches: ErrorOccurrence[];
}

/** The current co-play model's summary, as the admin co-play config reports it. */
export interface CoplayModelSummary {
  version: string;
  fitted_at: string;
  n_decks: number;
  n_items: number;
  scale: number;
  holdout_hit_at_20: number | null;
  row_weight: string;
  k: number;
}

/** GET/PUT/DELETE /api/v1/admin/config/coplay: the Find-a-Home co-play weight knob. */
export interface CoplayConfig {
  /** The weight Find a Home applies (0 = co-play off). */
  weight: number;
  /** The code default the weight falls back to when no override is stored. */
  default: number;
  is_override: boolean;
  /** The largest weight the server accepts (the range is 0..max); the UI renders this bound, never its own. */
  max: number;
  /** null when the server holds no co-play model. */
  model: CoplayModelSummary | null;
}

/** GET /api/v1/admin/theme-map: the EDHREC global theme harvest and the mined theme map. */
export interface ThemeMapStatus {
  version: string | null;
  harvested_at: string | null;
  mined_at: string | null;
  /** Themes per kind: typal, tags, playstyle, role, unresolved, unmapped, unharvested. */
  counts: Record<string, number>;
  /** Theme slugs EDHREC lists that have no harvest yet. */
  new_slugs: string[];
}

export const admin = {
  async getErrorGroups(days: number): Promise<{ days: number; groups: ErrorGroup[] }> {
    const r = await apiFetch(`/api/v1/admin/errors?days=${days}`);
    return ok(r, 'Failed to load the exception log');
  },

  async getErrorOccurrences(fingerprint: string, limit = 20): Promise<{ fingerprint: string; occurrences: ErrorOccurrence[] }> {
    const q = new URLSearchParams({ fingerprint, limit: String(limit) });
    const r = await apiFetch(`/api/v1/admin/errors/occurrences?${q}`);
    return ok(r, 'Failed to load the occurrences');
  },

  /** `raw` goes as typed (e.g. `(ref 1A2B3C4D)`): the backend normalises it, and answers 422 when no ref is in it. */
  async findErrorRef(raw: string): Promise<ErrorRefSearch> {
    const r = await apiFetch(`/api/v1/admin/errors/ref/${encodeURIComponent(raw)}`);
    return ok<ErrorRefSearch>(r, 'Failed to search for the ref');
  },

  async getOverview(): Promise<OverviewStats> {
    const r = await apiFetch('/api/v1/admin/overview');
    return ok<OverviewStats>(r, 'Failed to load admin overview');
  },

  async listUsers(): Promise<AdminUser[]> {
    const r = await apiFetch('/api/v1/admin/users');
    const data = await ok<{ users: AdminUser[] }>(r, 'Failed to list users');
    return data.users;
  },

  async updateUserRole(userId: string, role: string): Promise<AdminUser> {
    const r = await apiFetch(`/api/v1/admin/users/${userId}/role`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ role }),
    });
    return ok<AdminUser>(r, 'Failed to update role');
  },

  async addUserCredits(userId: string, amount: number): Promise<{ credits_used: number }> {
    const r = await apiFetch(`/api/v1/admin/users/${userId}/credits`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ amount }),
    });
    return ok<{ credits_used: number }>(r, 'Failed to add credits');
  },

  async refreshPowerIndicators(): Promise<{ status: string }> {
    const r = await apiFetch('/api/v1/admin/refresh-power-indicators', { method: 'POST' });
    return ok<{ status: string }>(r, 'Failed to refresh power indicators');
  },

  async getCardsStatus(): Promise<{ card_count: number; is_stale: boolean }> {
    const r = await apiFetch('/api/v1/admin/cards/status');
    return ok<{ card_count: number; is_stale: boolean }>(r, 'Failed to get card status');
  },

  async refreshCards(): Promise<{ task_id: string }> {
    const r = await apiFetch('/api/v1/admin/cards/refresh', { method: 'POST' });
    return ok<{ task_id: string }>(r, 'Failed to refresh cards');
  },

  async refreshRecentSets(): Promise<{ task_id: string }> {
    const r = await apiFetch('/api/v1/admin/cards/refresh-sets', { method: 'POST' });
    return ok<{ task_id: string }>(r, 'Failed to start the recent-sets refresh');
  },

  async getThemeMapStatus(): Promise<ThemeMapStatus> {
    const r = await apiFetch('/api/v1/admin/theme-map');
    return ok<ThemeMapStatus>(r, 'Failed to get the theme map status');
  },

  async refreshThemeMap(): Promise<{ task_id: string }> {
    const r = await apiFetch('/api/v1/admin/theme-map/refresh', { method: 'POST' });
    return ok<{ task_id: string }>(r, 'Failed to start the theme harvest');
  },

  async getLLMStats(days = 30): Promise<LLMUsageStat[]> {
    const r = await apiFetch(`/api/v1/admin/llm-stats?days=${days}`);
    const data = await ok<{ stats: LLMUsageStat[] }>(r, 'Failed to get LLM stats');
    return data.stats;
  },

  async getRecentLLMUsage(limit = 50): Promise<LLMUsageRecord[]> {
    const r = await apiFetch(`/api/v1/admin/llm-stats/recent?limit=${limit}`);
    const data = await ok<{ records: LLMUsageRecord[] }>(r, 'Failed to get recent LLM usage');
    return data.records;
  },

  async rerunAnalysis(username?: string): Promise<{ queued: number; username: string | null }> {
    const r = await apiFetch('/api/v1/admin/rerun-analysis', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username: username ?? '' }),
    });
    return ok<{ queued: number; username: string | null }>(r, 'Failed to queue re-analysis');
  },

  async clearTaskQueue(): Promise<{ cancelled: number; discarded: number }> {
    const r = await apiFetch('/api/v1/admin/tasks', { method: 'DELETE' });
    return ok<{ cancelled: number; discarded: number }>(r, 'Failed to clear task queue');
  },

  async cancelTask(taskId: string): Promise<{ cancelled: boolean }> {
    const r = await apiFetch(`/api/v1/admin/tasks/${taskId}`, { method: 'DELETE' });
    return ok<{ cancelled: boolean }>(r, 'Failed to cancel task');
  },

  async getRecentAnalysisRuns(limit = 10): Promise<AnalysisRun[]> {
    const r = await apiFetch(`/api/v1/admin/analysis-runs?limit=${limit}`);
    const data = await ok<{ runs: AnalysisRun[] }>(r, 'Failed to load analysis runs');
    return data.runs;
  },

  async listDasRuns(limit = 50, offset = 0): Promise<DasRun[]> {
    const r = await apiFetch(`/api/v1/admin/dec-a-sketch/runs?limit=${limit}&offset=${offset}`);
    const data = await ok<{ runs: DasRun[] }>(r, 'Failed to list DaS runs');
    return data.runs;
  },

  async getDasRun(runId: number): Promise<DasRun> {
    const r = await apiFetch(`/api/v1/admin/dec-a-sketch/runs/${runId}`);
    return ok<DasRun>(r, 'Failed to get DaS run');
  },

  async updateUserRoles(userId: string, roles: string[]): Promise<{ roles: string[] }> {
    const r = await apiFetch(`/api/v1/admin/users/${userId}/roles`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ roles }),
    });
    return ok<{ roles: string[] }>(r, 'Failed to update roles');
  },

  async listFeatureFlags(): Promise<AdminFeatureFlag[]> {
    const r = await apiFetch('/api/v1/admin/feature-flags');
    const data = await ok<{ flags: AdminFeatureFlag[] }>(r, 'Failed to list feature flags');
    return data.flags;
  },

  async upsertFeatureFlag(flag_name: string, role: string, enabled: boolean): Promise<AdminFeatureFlag> {
    const r = await apiFetch('/api/v1/admin/feature-flags', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ flag_name, role, enabled }),
    });
    return ok<AdminFeatureFlag>(r, 'Failed to update feature flag');
  },

  async getModelConfig(): Promise<{ active: string; models: string[]; env_default: string; is_override: boolean }> {
    const r = await apiFetch('/api/v1/admin/config/model');
    return ok<{ active: string; models: string[]; env_default: string; is_override: boolean }>(r, 'Failed to get model config');
  },

  async setActiveModel(model: string): Promise<{ active: string; models: string[]; env_default: string; is_override: boolean }> {
    const r = await apiFetch('/api/v1/admin/config/model', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ model }),
    });
    return ok<{ active: string; models: string[]; env_default: string; is_override: boolean }>(r, 'Failed to set active model');
  },

  async getCoplayConfig(): Promise<CoplayConfig> {
    const r = await apiFetch('/api/v1/admin/config/coplay');
    return ok<CoplayConfig>(r, 'Failed to get co-play config');
  },

  async setCoplayWeight(weight: number): Promise<CoplayConfig> {
    const r = await apiFetch('/api/v1/admin/config/coplay', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ weight }),
    });
    return ok<CoplayConfig>(r, 'Failed to set co-play weight');
  },

  async clearCoplayWeight(): Promise<CoplayConfig> {
    const r = await apiFetch('/api/v1/admin/config/coplay', { method: 'DELETE' });
    return ok<CoplayConfig>(r, 'Failed to clear co-play weight');
  },

  async getTagGroupingConfig(): Promise<{ theme_fit_ratio: number; is_default: boolean }> {
    const r = await apiFetch('/api/v1/admin/config/tag-grouping');
    return ok<{ theme_fit_ratio: number; is_default: boolean }>(r, 'Failed to get tag grouping config');
  },

  async setTagGroupingConfig(theme_fit_ratio: number): Promise<{ theme_fit_ratio: number; is_default: boolean }> {
    const r = await apiFetch('/api/v1/admin/config/tag-grouping', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ theme_fit_ratio }),
    });
    return ok<{ theme_fit_ratio: number; is_default: boolean }>(r, 'Failed to set tag grouping config');
  },

  async clearActiveModel(): Promise<{ active: string; models: string[]; env_default: string; is_override: boolean }> {
    const r = await apiFetch('/api/v1/admin/config/model', { method: 'DELETE' });
    return ok<{ active: string; models: string[]; env_default: string; is_override: boolean }>(r, 'Failed to clear model override');
  },

  async addModelToList(model: string): Promise<{ models: string[] }> {
    const r = await apiFetch('/api/v1/admin/config/model/list', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ model }),
    });
    return ok<{ models: string[] }>(r, 'Failed to add model');
  },

  async getRulingsStatus(): Promise<{ count: number; last_imported_at: string | null }> {
    const r = await apiFetch('/api/v1/admin/rulings/status');
    return ok(r, 'Failed to load rulings status');
  },

  async refreshRulings(): Promise<{ task_id: string }> {
    const r = await apiFetch('/api/v1/admin/rulings/refresh', { method: 'POST' });
    return ok(r, 'Failed to refresh rulings');
  },

  async getRulesStatus(): Promise<{ count: number; last_imported_at: string | null }> {
    const r = await apiFetch('/api/v1/admin/rules/status');
    return ok(r, 'Failed to load rules status');
  },

  async refreshRules(): Promise<{ task_id: string }> {
    const r = await apiFetch('/api/v1/admin/rules/refresh', { method: 'POST' });
    return ok(r, 'Failed to refresh rules');
  },

  async getCombosStatus(): Promise<CombosStatus> {
    const r = await apiFetch('/api/v1/admin/combos/status');
    return ok<CombosStatus>(r, 'Failed to load Spellbook sync status');
  },

  async refreshCombos(): Promise<{ task_id: string }> {
    const r = await apiFetch('/api/v1/admin/combos/refresh', { method: 'POST' });
    return ok(r, 'Failed to start the Spellbook sync');
  },

  async refreshCorpus(): Promise<{ task_id: string }> {
    const r = await apiFetch('/api/v1/admin/corpus/refresh', { method: 'POST' });
    return ok(r, 'Failed to start the corpus crawl');
  },

  async refreshWhatsNew(): Promise<{ task_id: string }> {
    const r = await apiFetch('/api/v1/admin/whats-new/refresh', { method: 'POST' });
    return ok(r, "Failed to start the What's New refresh");
  },

  async getComboBackfill(): Promise<ComboBackfillReports> {
    const r = await apiFetch('/api/v1/admin/combos/backfill');
    return ok<ComboBackfillReports>(r, 'Failed to load the combo backfill reports');
  },

  /** 409 (an ApiError carrying the reason) when the run may not start or a backfill is already running. */
  async runComboBackfill(mode: 'preview' | 'apply'): Promise<{ task_id: string; mode: 'preview' | 'apply' }> {
    const r = await apiFetch(`/api/v1/admin/combos/backfill/${mode}`, { method: 'POST' });
    return ok(r, `Failed to start the combo backfill (${mode})`);
  },

  // ── Game Engine (Advanced) ──────────────────────────────────────────
  async listUnresolvedCalls(): Promise<UnresolvedCallsReport> {
    const r = await apiFetch('/api/v1/admin/engine/gaps/unresolved-calls');
    return ok<UnresolvedCallsReport>(r, 'Failed to load unresolved calls');
  },

  async engineDeckCoverage(): Promise<AdminDeckCoverageRow[]> {
    const r = await apiFetch('/api/v1/admin/engine/deck-coverage');
    const data = await ok<{ decks: AdminDeckCoverageRow[] }>(r, 'Failed to load deck engine coverage');
    return data.decks;
  },

  async engineDeckCoverageDetail(deckId: string): Promise<AdminDeckCoverageDetail> {
    const r = await apiFetch(`/api/v1/admin/engine/deck-coverage/${deckId}`);
    return ok<AdminDeckCoverageDetail>(r, 'Failed to load deck coverage detail');
  },

  async engineCompileMissing(deckId: string): Promise<AdminCompileMissingResult> {
    const r = await apiFetch(`/api/v1/admin/engine/deck-coverage/${deckId}/compile-missing`, { method: 'POST' });
    return ok<AdminCompileMissingResult>(r, 'Failed to start compile-missing task');
  },
};

export interface UnresolvedCallEntry {
  slot: string;
  keyword: string;
}

export interface UnresolvedCallsItem {
  card_name: string;
  compiler_version: string | null;
  parse_quality: string | null;
  unresolved: UnresolvedCallEntry[];
}

export interface UnresolvedCallsReport {
  total_cards: number;
  unique_keywords: string[];
  items: UnresolvedCallsItem[];
}

export interface AdminDeckCoverageRow {
  deck_id: string;
  deck_name: string;
  unique_ct: number;
  compiled_ct: number;
  missing_ct: number;
  pct: number;
  complete: boolean;
}

export interface AdminDeckCoverageDetail {
  deck_id: string;
  deck_name: string;
  unique_ct: number;
  compiled_ct: number;
  missing: string[];
  pct: number;
  complete: boolean;
}

export interface AdminCompileMissingResult {
  task_id: string;
  cards: string[];
}

// ─── Misc ─────────────────────────────────────────────────────────────────────

export const misc = {
  async getStats(): Promise<StatsData> {
    const r = await apiFetch('/api/v1/stats');
    return ok<StatsData>(r, 'Failed to get stats');
  },

  async getGameChangers(): Promise<{ cards: string[] }> {
    const r = await apiFetch('/api/v1/game-changers');
    return ok<{ cards: string[] }>(r, 'Failed to get game changers');
  },

  async getHealth(): Promise<HealthData> {
    const r = await apiFetch('/api/health');
    return ok<HealthData>(r, 'Failed to get health');
  },
};

// ─── Cards ───────────────────────────────────────────────────────────────────

export interface AutocompleteCard {
  name: string;
  type_line?: string;
  game_changer?: boolean;
  /** WUBRG letters; GET /cards/autocomplete always sends it (voice-match does not). */
  color_identity?: string[];
}

export interface CardStatusResult {
  game_changer: boolean;
  banned: boolean;
  unreleased: boolean;
}

export interface CardPrinting {
  scryfall_id: string;
  set_code: string;
  set_name: string;
  collector_number: string;
  released_at: string | null;
  price_usd: number | null;
  price_usd_foil: number | null;
  has_foil: boolean;
  has_nonfoil: boolean;
  image_uri: string | null;
}

export const cards = {
  async autocomplete(q: string, ci?: string, limit?: number, commanderOnly?: boolean): Promise<{ cards: AutocompleteCard[] }> {
    const params = new URLSearchParams({ q });
    if (ci) params.set('ci', ci);
    if (limit) params.set('limit', String(limit));
    if (commanderOnly) params.set('commander_only', 'true');
    const r = await apiFetch(`/api/v1/cards/autocomplete?${params}`);
    return ok<{ cards: AutocompleteCard[] }>(r, 'Autocomplete failed');
  },

  async printings(name: string): Promise<{ name: string; printings: CardPrinting[] }> {
    const params = new URLSearchParams({ name });
    const r = await apiFetch(`/api/v1/cards/printings?${params}`);
    return ok<{ name: string; printings: CardPrinting[] }>(r, 'Failed to load printings');
  },

  async status(names: string[]): Promise<{ results: Record<string, CardStatusResult> }> {
    const r = await apiFetch(`/api/v1/cards/status?names=${names.map(encodeURIComponent).join(',')}`);
    return ok<{ results: Record<string, CardStatusResult> }>(r, 'Failed to get card status');
  },

  async getDetail(name: string): Promise<CardDetail> {
    const r = await apiFetch(`/api/v1/cards/detail?name=${encodeURIComponent(name)}`);
    return ok<CardDetail>(r, 'Failed to get card detail');
  },

  async voiceMatch(transcript: string, ci?: string, limit?: number): Promise<{ cards: AutocompleteCard[] }> {
    const params = new URLSearchParams({ q: transcript });
    if (ci) params.set('ci', ci);
    if (limit) params.set('limit', String(limit));
    const r = await apiFetch(`/api/v1/cards/voice-match?${params}`);
    return ok<{ cards: AutocompleteCard[] }>(r, 'Voice match failed');
  },
};

export interface CardDetail {
  found: boolean;
  name?: string;
  oracle_text?: string;
  mana_cost?: string;
  type_line?: string;
  type_data?: Record<string, string>;  // type-specific stats: power, toughness, loyalty, defense, etc.
  color_identity?: string[];
  rulings: Array<{ published_at: string; comment: string }>;
}

// ─── Guided deck builder ──────────────────────────────────────────────────────

export interface CommanderTheme { slug: string; label: string; count: number; is_typal: boolean }

export interface BuildRole { role: string; target: number; current: number; cards: string[] }

/** A Game Changer scored by the incumbent score at its best role (backend `bracket_fit.ScoredCard`). */
export interface BracketFitCard { oracle_id: string; name: string; score: number; plan_fit: number; value: number }

/** The build's fit to its target bracket (`GET /decks/{id}/build/bracket-fit`): empty without a target below the floor.
 *  The heading, the rule-break intro and the keep label are the backend's text, rendered verbatim. */
export interface BracketFit {
  target: number | null;
  floor: number;
  heading: string | null;
  rule_breaks_intro: string | null;
  rule_breaks: FloorReason[];
  game_changers: { allowance: number; keep_label: string; keep: BracketFitCard[]; cut: BracketFitCard[] } | null;
}

/** A chosen theme's share of the Theme row (backend `fill_run.themes_detail`). `reason` says why it cannot be filled. */
export interface ThemeDetail {
  slug: string; label: string; kind: string | null; target: number; held: number; gap: number;
  fillable: boolean; reason: string | null;
  /** The most cards the theme can hold: held plus the build's nonland room (`min(room, free_slots)`). */
  max: number;
  /** The owner stored this target (a slider) rather than the default. */
  explicit: boolean;
  /** Frontend-only: a theme just toggled on, shown as a placeholder until the write's payload lands. */
  pending?: boolean;
}

/** What to fill: a row (`slot`), a chosen theme (`theme`), or every short row. */
export type FillRequest =
  | { kind: 'row'; slot: string }
  | { kind: 'theme'; theme: string }
  | { kind: 'all_short' };

export interface FillResult {
  placed: string[];
  dropped: { name: string; reason: string }[];
  /** full | no members | theme ran out | row ran out | gap closed; null when the fill met its target. */
  stopped: string | null;
  revision_label: string | null;
  total_cards: number;
  unique_cards: number;
}

/** POST /build/theme-target: store a theme's target, then fill to it (`fill`); `none` when it equals the held
 *  count. A target below the held count is a 400. `stopped` is the fill's reason it fell short ('full', ...). */
export interface ThemeTargetResult {
  action: 'fill' | 'none';
  placed: string[];
  dropped: { name: string; reason: string }[];
  stopped: string | null;
  revision_label: string | null;
  total_cards: number;
  unique_cards: number;
}

/** One held member of a theme in cut order (weakest first); `tier` 0 = free, 1 = serves another theme / a needed role /
 *  a one-short combo, 2 = a piece of an assembled combo. Starred cards sort last and are never cut. */
export interface CutOrderEntry {
  name: string;
  score: number;
  starred: boolean;
  tier: 0 | 1 | 2;
  themes: string[];
  roles: string[];
  combo: 'assembled' | 'one_short' | null;
  /** Whole days since the newest trial add of this card; null when it is not a trial. */
  trial_age_days: number | null;
}

/** GET /build/cut-order: the theme's held members in the order a cut would take them. */
export interface CutOrder {
  theme: string;
  held: number;
  target: number;
  order: CutOrderEntry[];
}


/** Backend `SwapSource` (metacommand/web/models.py): the door a swap was made from. */
export type SwapSource = 'build' | 'whats-new' | 'find-home' | 'browse' | 'combo' | 'evaluate';

/** Backend `SwapConsequence` (routers/build.py): `ConsequenceKind` value + its subject. */
export interface Consequence {
  kind: 'row_short' | 'theme_drops' | 'combo_breaks' | 'none';
  subject: string | null;
}

/** Backend `SwapFacts`: a card as this deck sees it. `roles`/`rows_short` are template row keys. */
export interface CardFacts {
  oracle_id: string;
  name: string;
  mana_cost: string;
  roles: string[];
  rows_short: string[];
  themes: string[];
  band: string;
  /** The deck's current nonland share in `band` (0..1). */
  band_share: number;
  /** The band's reference share (backend `sketch_fill.BAND_SHARES`, 0..1). */
  band_target: number;
  /** `ComboHold` value, or null. */
  combo: string | null;
  /** X only: the rank assembly's score for X against this deck; null without a stored analysis (and for every candidate). */
  synergy: number | null;
  synergy_presence: SynergyPresence | null;
}

/** Backend `SwapCandidate`, ordered by X's row group then blend. `source`, `tier`, `damage`, `trial_age_days` and
 *  `consequence` are tags, never the order. `damage` is null when the proposal is degraded; `trial_age_days` only on a
 *  recent trial add. */
export interface CutCandidate {
  facts: CardFacts;
  source: 'same_role' | 'weakest' | 'other';
  tier: number;
  damage: number | null;
  blend: number;
  trial_age_days: number | null;
  consequence: Consequence;
}

/** Backend `SwapProposalResult`: GET /decks/{id}/build/swap. */
export interface SwapProposal {
  x: CardFacts;
  /** The fill's nonland room, for display; `room_for_x` is the gate. */
  room: number;
  /** Cards the deck may take before 100 (the command zone counts). */
  free_slots: number;
  /** The gate on adding X without a cut: `free_slots` for a land X, `min(room, free_slots)` for a nonland one. */
  room_for_x: number;
  degraded: boolean;
  /** Backend `XGain`: `none` when X has no template row, no selected theme, no short row and no positive co-play. */
  gain: 'measured' | 'none';
  candidates: CutCandidate[];
  not_offered: { name: string; reason: string }[];
  /** Backend `SwapMode`: what the cuts are (`basics`: a land X, basics above their reserve). */
  mode: 'nonland' | 'land_under_floor' | 'basics';
}

export interface BuildPayload {
  build_id: string;
  /** What the deck's own cards read as; `[]` when the deck is too thin to read or no theme map exists yet. */
  reads_as: SynergyTheme[];
  /** Game changers present in THIS build's resolved list — both boards — sorted by name. */
  game_changers: string[];
  /** The subset of `game_changers` that sit on the command board: shown, never cuttable. */
  game_changer_commanders: string[];
  /** The same bracket-benchmark callouts the bracket tooltip shows. */
  bracket_read_callouts?: BracketReadCallout[];
  bracket: number;
  commander: string[];
  color_identity: string[];
  themes: string[];
  /** The Theme row: its size; the claim (the chosen themes' targets plus `rest`, held cards that fit no row and are no
   *  theme's member); `unassigned = size - claim`; `to_roles`, the slots the role rows actually take. */
  theme_row: { size: number; claim: number; rest: number; unassigned: number; to_roles: number };
  land_floor: number;
  /** The fill's nonland room (the land floor's slots kept), for display: never a gate alone (`nonlandRoom`). */
  room: number;
  /** Cards the deck may take before 100 (the command zone counts): a land add without a cut needs one. */
  free_slots: number;
  game_changer_cap: number | null;
  slot_overrides: Record<string, number>;
  roles: BuildRole[];
  themes_detail?: ThemeDetail[];
  /** The cards the latest fill placed, with its revision label; null with no fill yet. */
  latest_fill?: { label: string; placed: string[] } | null;
}

// ─── Goals & Fit (build inspection) ───────────────────────────────────────────

export type FacetKey = 'speed' | 'consistency' | 'interaction' | 'closing';

/** The owner's stated goals for a build. Positions, not limits: absent = the bracket's median. */
export interface GoalEnvelope {
  facet_targets: Partial<Record<FacetKey, number>>;
  budget_per_card_usd: number | null;
  salt_tolerance: number | null;
  operational_threshold: number | null;
  deck_budget_usd: number | null;
}

export interface FacetFenceRegion { p10: number; p50: number; p90: number; n: number; low_confidence: boolean }

export type FacetTargetSource = 'goal' | 'fence_median' | 'none';

export interface FacetInspection {
  value: number;
  target: number | null;
  gap: number;
  source: FacetTargetSource;
  degraded: boolean;
  fence: FacetFenceRegion | null;
}

export interface GoalsInspection {
  build_id: string;
  bracket: number;
  /** True when the numbers answer a `trial_goals` what-if rather than the saved goals. */
  trial: boolean;
  /** The envelope the numbers were computed against (saved, or the trial); null = none stored. */
  goals: GoalEnvelope | null;
  facets: Record<FacetKey, FacetInspection>;
  advisory: { bracket: number; position: string; n: number } | null;
  budget_fit: string;
  salt_fit: string;
  deck_budget_fit: string;
  deck_price_usd: number;
  unpriced_cards: number;
  engines_online_turn: number | null;
  operational_threshold: number | null;
  threshold_fit: 'ok' | 'late' | 'unknown' | null;
  degraded: boolean;
  inputs_stale: boolean;
}

export interface SuggestionComponents {
  role: number; vu: number; theme: number; synergy: number; combo: number; popularity: number; plan_fit: number;
  /** The co-play value; 0.0 unless co-play reached the card (then synergy_presence is 'coplay' and synergy is 0.0). */
  coplay: number;
  /** EDHREC inclusion, 0..1: the other half of the blend (0 with no EDHREC page). */
  inclusion: number;
  plan_receipts?: PlanReceipts | null;
}

export interface Suggestion {
  name: string;
  oracle_id: string;
  roles: Record<string, number>;
  /** The 0..1 blend: half EDHREC inclusion, half co-play (co-play alone with no page). */
  score: number;
  components: SuggestionComponents;
  /** An earned spicy pick; `spicy_text` is the backend-composed tooltip, present only when true. */
  spicy?: boolean;
  spicy_text?: string;
  synergy_presence: SynergyPresence;
  synergy_receipts?: SynergyReceipt[];
  vu: number | null;
  price_usd: number | null;
  game_changer: boolean;
  mana_cost: string;
  type_line: string;
}

export type SuggestSort = 'score' | 'vu';

/** `land`: a front-face land piece; it goes through the same cut as any other piece (the proposal takes lands). */
export interface ComboPiece { name: string; present: boolean; price_usd: number | null; must_be_commander: boolean; resolved: boolean; land: boolean }

export interface ComboOffer {
  variant_id: string;
  bracket: number | null;
  popularity: number | null;
  features: string[];
  description: string;
  present_count: number;
  /** assembled = every piece in the deck; partial = some; offer = none. Offers arrive sorted by tier. */
  tier: 'assembled' | 'partial' | 'offer';
  pieces: ComboPiece[];
}

export interface LandCycleStatus { key: string; label: string; tier: number; allowed: boolean; fitting: string[]; present: string[] }

export interface LandsPayload {
  land_floor: number;
  land_count: number;
  /** Every held basic with its pip-weighted reserve (`landbase.basics_split`). */
  basics: { name: string; held: number; reserve: number }[];
  cycles: LandCycleStatus[];
}

export interface FillResult {
  steps: { label: string; names: string[] }[];
  basics: Record<string, number>;
  added: number;
  total_cards: number;
  unique_cards: number;
}

export interface CardsMutationResult { total_cards: number; unique_cards: number; added?: string[] }

/** The latest stored AI tuning for one build (`build_id` is 'base' or a variant id). */
export interface TuningPayload {
  build_id: string;
  tuning: {
    current_assessment: PowerLevelResult;
    tuning_recommendations: { tuning_text: string; target_bracket: number | null };
  } | null;
  stale: boolean;
  analyzed_at: string | null;
}

const jsonBody = (method: 'POST' | 'PUT' | 'DELETE', body?: unknown): RequestInit => ({
  method,
  headers: { 'Content-Type': 'application/json' },
  ...(body === undefined ? {} : { body: JSON.stringify(body) }),
});

/** Every builder route is scoped by `build_id` — 'base' or a variant id (2026-09-20 spec D1). */
function withBuild(path: string, buildId: string, params?: URLSearchParams): string {
  const p = new URLSearchParams(params ?? undefined);   // a copy: never mutate the caller's params
  p.set('build_id', buildId);
  return `${path}?${p}`;
}

export const build = {
  async commanderThemes(name: string): Promise<{ commander: string; slug: string; themes: CommanderTheme[] }> {
    const r = await apiFetch(`/api/v1/commanders/themes?name=${encodeURIComponent(name)}`);
    return ok(r, 'Failed to load commander themes');
  },

  /** The deck's theme menu, from its command zone's own EDHREC page (a partner pair's included) —
   *  the page theme discovery reads. Deck-level: every build shares it. */
  async deckThemes(deckId: string): Promise<{ commander: string; slug: string; themes: CommanderTheme[] }> {
    const r = await apiFetch(`/api/v1/decks/${deckId}/build/themes`);
    return ok(r, 'Failed to load themes');
  },

  /** The build's plan as the Build tab's deck-level line; null with no archetype. */
  async plan(deckId: string, buildId: string): Promise<{ plan: PlanSummary | null }> {
    const r = await apiFetch(withBuild(`/api/v1/decks/${deckId}/build/plan`, buildId));
    return ok(r, 'Failed to load the plan');
  },

  /** The hard rules the build breaks at its target and the Game Changers worth keeping; empty at or above the floor. */
  async bracketFit(deckId: string, buildId: string): Promise<BracketFit> {
    const r = await apiFetch(withBuild(`/api/v1/decks/${deckId}/build/bracket-fit`, buildId));
    return ok<BracketFit>(r, 'Failed to load the bracket fit');
  },

  async get(deckId: string, buildId: string): Promise<BuildPayload> {
    const r = await apiFetch(withBuild(`/api/v1/decks/${deckId}/build`, buildId));
    return ok<BuildPayload>(r, 'Failed to load the build');
  },

  /** Fold the build against its goals; `trialGoals` is a what-if that REPLACES the saved goals. */
  async inspect(deckId: string, buildId: string, trialGoals?: GoalEnvelope): Promise<GoalsInspection> {
    const r = await apiFetch(`/api/v1/decks/${deckId}/build/inspect`,
      jsonBody('POST', { build_id: buildId, trial_goals: trialGoals ?? null }));
    return ok<GoalsInspection>(r, 'Failed to inspect the build');
  },

  /** null clears (a variant falls back to the deck's goals); `{}`-shaped empty envelope = "no goals here". */
  async setGoals(deckId: string, buildId: string, goals: GoalEnvelope | Record<string, never> | null): Promise<{ build_id: string; goals: GoalEnvelope | null }> {
    const r = await apiFetch(withBuild(`/api/v1/decks/${deckId}/build/goals`, buildId), jsonBody('PUT', goals));
    return ok(r, 'Failed to save the goals');
  },

  /** Free, deterministic verdicts for up to 15 typed card names. */
  async evaluateCards(deckId: string, buildId: string, cards: string[]): Promise<EvaluateCardsResult> {
    const r = await apiFetch(`/api/v1/decks/${deckId}/build/evaluate-cards`,
      jsonBody('POST', { build_id: buildId, cards }));
    return ok<EvaluateCardsResult>(r, 'Failed to evaluate the cards');
  },

  /** One audited paragraph on the verdicts; 402 (ApiError) when out of credits. */
  async narrateEvaluations(deckId: string, buildId: string, cards: string[]): Promise<{ why: string | null }> {
    const r = await apiFetch(`/api/v1/decks/${deckId}/build/evaluate-cards/narrate`,
      jsonBody('POST', { build_id: buildId, cards }));
    return ok<{ why: string | null }>(r, 'Failed to explain the verdicts');
  },

  async tuning(deckId: string, buildId: string): Promise<TuningPayload> {
    const r = await apiFetch(withBuild(`/api/v1/decks/${deckId}/tuning`, buildId));
    return ok<TuningPayload>(r, 'Failed to load the tuning result');
  },

  /** `themes` omitted = the deck's themes are left untouched (a slot-override save); sent = the
   *  owner's theme choice (a changed list marks the deck's themes chosen, never seeded again). */
  async setTemplate(deckId: string, buildId: string, body: TemplateWrite): Promise<BuildPayload> {
    const r = await apiFetch(withBuild(`/api/v1/decks/${deckId}/build/template`, buildId), jsonBody('PUT', body));
    return ok<BuildPayload>(r, 'Failed to save the template');
  },

  /** Fill answers in the request: the placed and dropped cards and why it stopped short. */
  async fill(deckId: string, buildId: string, body: FillRequest): Promise<FillResult> {
    const r = await apiFetch(withBuild(`/api/v1/decks/${deckId}/build/fill`, buildId), jsonBody('POST', body));
    return ok<FillResult>(r, 'Failed to fill');
  },

  /** One slider move: stores the theme's target, then fills to it. 400 carries the reason. */
  async setThemeTarget(deckId: string, buildId: string, theme: string, target: number): Promise<ThemeTargetResult> {
    const r = await apiFetch(withBuild(`/api/v1/decks/${deckId}/build/theme-target`, buildId), jsonBody('POST', { theme, target }));
    return ok<ThemeTargetResult>(r, 'Failed to set the theme target');
  },

  /** To add `name`, every cut that could pay for it, in order (read-only). */
  async swapProposal(deckId: string, buildId: string, name: string): Promise<SwapProposal> {
    const r = await apiFetch(withBuild(`/api/v1/decks/${deckId}/build/swap`, buildId, new URLSearchParams({ in: name })));
    return ok<SwapProposal>(r, 'Failed to load the swap proposal');
  },

  /** The theme's held members, weakest first (read-only). */
  async cutOrder(deckId: string, buildId: string, theme: string): Promise<CutOrder> {
    const r = await apiFetch(withBuild(`/api/v1/decks/${deckId}/build/cut-order`, buildId, new URLSearchParams({ theme })));
    return ok<CutOrder>(r, 'Failed to load the cut order');
  },

  async setBracket(deckId: string, buildId: string, bracket: number): Promise<BuildPayload> {
    const r = await apiFetch(withBuild(`/api/v1/decks/${deckId}/build/bracket`, buildId), jsonBody('PUT', { bracket }));
    return ok<BuildPayload>(r, 'Failed to set the bracket');
  },

  /** One page of ranked adds: a role row's (`role`), a theme's members not held (`theme`), or the deck's (neither).
   *  `role` and `theme` are exclusive (the route answers 400). */
  async suggest(
    deckId: string, buildId: string,
    { role, theme, sort, limit = 20 }: { role?: string | null; theme?: string; sort: SuggestSort; limit?: number },
  ): Promise<{ role: string | null; theme?: string | null; sort: SuggestSort; plan: PlanSummary | null; suggestions: Suggestion[] }> {
    const params = new URLSearchParams();
    if (role) params.set('role', role);
    if (theme) params.set('theme', theme);
    params.set('sort', sort);
    params.set('limit', String(limit));
    const r = await apiFetch(withBuild(`/api/v1/decks/${deckId}/build/suggest`, buildId, params));
    return ok(r, 'Failed to load suggestions');
  },

  async addCards(deckId: string, buildId: string, cardsToAdd: { name: string; qty: number }[], label?: string): Promise<CardsMutationResult> {
    const r = await apiFetch(withBuild(`/api/v1/decks/${deckId}/cards`, buildId), jsonBody('POST', { cards: cardsToAdd, label: label ?? null }));
    return ok<CardsMutationResult>(r, 'Failed to add cards');
  },

  async combos(deckId: string, buildId: string): Promise<{ offers: ComboOffer[] }> {
    const r = await apiFetch(withBuild(`/api/v1/decks/${deckId}/build/combos`, buildId));
    return ok<{ offers: ComboOffer[] }>(r, 'Failed to load combo offers');
  },

  async addCombo(deckId: string, buildId: string, variantId: string): Promise<CardsMutationResult> {
    const r = await apiFetch(withBuild(`/api/v1/decks/${deckId}/build/combos/${encodeURIComponent(variantId)}`, buildId), jsonBody('POST'));
    return ok<CardsMutationResult>(r, 'Failed to add the combo');
  },

  async lands(deckId: string, buildId: string): Promise<LandsPayload> {
    const r = await apiFetch(withBuild(`/api/v1/decks/${deckId}/build/lands`, buildId));
    return ok<LandsPayload>(r, 'Failed to load land cycles');
  },

  async addCycle(deckId: string, buildId: string, key: string): Promise<CardsMutationResult> {
    const r = await apiFetch(withBuild(`/api/v1/decks/${deckId}/build/lands/cycle/${encodeURIComponent(key)}`, buildId), jsonBody('POST'));
    return ok<CardsMutationResult>(r, 'Failed to add the land cycle');
  },

  async fillLands(deckId: string, buildId: string): Promise<FillResult> {
    const r = await apiFetch(withBuild(`/api/v1/decks/${deckId}/build/lands/fill`, buildId), jsonBody('POST'));
    return ok<FillResult>(r, 'Failed to fill lands');
  },

  async setPrinting(deckId: string, buildId: string, name: string, body: { scryfall_id: string; set: string; cn: string; finish: string }): Promise<{ ok: boolean }> {
    const r = await apiFetch(withBuild(`/api/v1/decks/${deckId}/cards/${encodeURIComponent(name)}/printing`, buildId), jsonBody('PUT', body));
    return ok<{ ok: boolean }>(r, 'Failed to set the printing');
  },
};

// ─── Games ────────────────────────────────────────────────────────────────────

export interface PodracerGamePlayer {
  deck_id: string | null;
  name: string;
  commander: string;
  bracket: number | null;
}

export interface PodracerGame {
  id: string;
  outcome: string; // 'winner:0' | 'winner:1' | 'winner:2' | 'winner:3' | 'draw'
  players: PodracerGamePlayer[];
  final_lives: number[];
  final_cd: number[][];
  is_public: boolean;
  created_at: string;
}

export const games = {
  async create(payload: {
    players: PodracerGamePlayer[];
    outcome: string;
    final_lives: number[];
    final_cd: number[][];
  }): Promise<{ id: string }> {
    const r = await apiFetch('/api/v1/games', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    return ok<{ id: string }>(r, 'Failed to save game');
  },

  async list(): Promise<{ games: PodracerGame[] }> {
    const r = await apiFetch('/api/v1/games');
    return ok<{ games: PodracerGame[] }>(r, 'Failed to load game history');
  },

  async setPublic(gameId: string, isPublic: boolean): Promise<void> {
    const r = await apiFetch(`/api/v1/games/${gameId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ is_public: isPublic }),
    });
    await ok(r, 'Failed to update game visibility');
  },
};

// ─── Game Sessions ────────────────────────────────────────────────────────────

export interface GameSessionPlayer {
  user_id: string;
  display_name: string;
  deck_id: string | null;
  commander: string | null;
  bracket: number;
}

export interface GameSession {
  id: string;
  players: GameSessionPlayer[];
  lives: number[];
  commander_damage: number[][];   // [attacker_idx][victim_idx]
  bracket: number;
  started_at: string;
  ended: boolean;
  outcome: string | null;         // 'winner:0' | 'winner:1' | ... | 'draw' | null
}

export const sessions = {
  async get(sessionId: string): Promise<{ session: GameSession }> {
    const r = await apiFetch(`/api/v1/sessions/${sessionId}`);
    return ok<{ session: GameSession }>(r, 'Failed to load session');
  },

  async updateLife(sessionId: string, playerIdx: number, newLife: number): Promise<void> {
    const r = await apiFetch(`/api/v1/sessions/${sessionId}/life`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ player_idx: playerIdx, new_life: newLife }),
    });
    await ok(r, 'Failed to update life');
  },

  async updateCd(sessionId: string, attacker: number, victim: number, newValue: number): Promise<void> {
    const r = await apiFetch(`/api/v1/sessions/${sessionId}/cd`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attacker, victim, new_value: newValue }),
    });
    await ok(r, 'Failed to update commander damage');
  },

  async end(sessionId: string, outcome: string): Promise<void> {
    const r = await apiFetch(`/api/v1/sessions/${sessionId}/end`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ outcome }),
    });
    await ok(r, 'Failed to end game');
  },

  wsUrl(sessionId: string, userId?: string): string {
    const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
    const host = window.location.host;
    const query = userId ? `?user_id=${encodeURIComponent(userId)}` : '';
    return `${proto}://${host}/api/v1/sessions/${sessionId}/ws${query}`;
  },
};

// ─── Queue ────────────────────────────────────────────────────────────────────

export interface QueueEntry {
  bracket: number;       // 1–5
  pod_size?: number;     // 3, 4, 5 — default 4
  deck_id?: string;
  queue_code?: string;   // private pod code
}

export interface LobbyPlayer {
  user_id: string;
  display_name: string;
  elo: number;
  bracket: number;
  deck_id: string | null;
  ready: boolean;
}

export interface PodLobby {
  lobby_id: string;
  players: LobbyPlayer[];
  target_size: number;
  deadline: string;      // ISO datetime
  status: 'ready_check' | 'confirmed' | 'cancelled';
  game_id: string | null;
}

export interface QueueState {
  status: 'idle' | 'queuing' | 'lobby';
  lobby?: PodLobby;
  position?: number;
  queue_code?: string;
}

async function apiPost<T>(url: string, body: unknown): Promise<T> {
  const r = await apiFetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  return ok<T>(r, `POST ${url} failed`);
}

export const queue = {
  join: (entry: QueueEntry) =>
    apiPost<{ ok: boolean }>('/api/v1/queue/join', entry),
  leave: () =>
    apiPost<{ ok: boolean }>('/api/v1/queue/leave', {}),
  ready: () =>
    apiPost<{ ok: boolean }>('/api/v1/queue/ready', {}),
  selectDeck: (deck_id: string) =>
    apiPost<{ ok: boolean }>('/api/v1/queue/select-deck', { deck_id }),
  invite: (display_name: string) =>
    apiPost<{ ok: boolean }>('/api/v1/queue/invite', { display_name }),
  getStatus: async (): Promise<QueueState> => {
    const r = await apiFetch('/api/v1/queue/status');
    return ok<QueueState>(r, 'Failed to get queue status');
  },
  getLobbies: async (): Promise<{ lobbies: PodLobby[] }> => {
    const r = await apiFetch('/api/v1/queue/lobbies');
    return ok<{ lobbies: PodLobby[] }>(r, 'Failed to get lobbies');
  },
};

// ─── Feature Flags ────────────────────────────────────────────────────────────

export async function getFeatures(): Promise<string[]> {
  const r = await apiFetch('/api/v1/features');
  if (!r.ok) return [];
  const data = await r.json();
  return data.features ?? [];
}

// ─── Podracer ─────────────────────────────────────────────────────────────────

export interface PodDeckDescriptor {
  type: 'id' | 'url' | 'text';
  value: string;
  name?: string;
  commander?: string | null;
}

export interface PodDeckResult {
  name: string;
  commander: string | null;
  bracket: number | null;
  color_identity: string[];
  game_changers: string[];
  combo_count: number;
  speed: string | null;
  themes: string[];
  card_count: number;
}

export interface PodracerResult {
  decks: PodDeckResult[];
  outlier_index: number;
  outlier_reason: string;
  roast: string;
}

export const podracer = {
  async analyze(decks: PodDeckDescriptor[]): Promise<PodracerResult> {
    const r = await apiFetch('/api/v1/podracer/analyze', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ decks }),
    });
    return ok<PodracerResult>(r, 'Pod analysis failed');
  },
};

// ─── Playtester ───────────────────────────────────────────────────────────────

// PlaytestCardState — removed in Sprint 3. Use CardData from @/types/game instead.
// Re-export for any legacy imports that haven't been updated yet.
export type { CardData as PlaytestCardState } from '@/types/game'

// PlaytestBoardState — removed in Sprint 3. Use ProjectedState from @/types/game.
export type { ProjectedState as PlaytestBoardState } from '@/types/game'

export interface PlaytestSessionData {
  id: string;
  deck_id: string;
  owner_id: string;
  state: ProjectedState;
  event_count: number;
  created_at: string;
}

export interface PlaytestTableData {
  table_id: string;
  table_code: string;
  status: 'lobby' | 'pregame' | 'in_progress' | 'ended';
  seat_index: number;
  state: ProjectedState;
}

export interface TableEventResult {
  event: {
    type: string
    seat: number
    metadata: Record<string, unknown>
    reveals_info: boolean
    sequence_num: number
  }
  /** Sprint 3: key renamed from table_state to state */
  state: ProjectedState
}

export const playtester = {
  tables: {
    async create(
      deck_id: string,
      display_name?: string,
      options?: { initial_hand?: unknown; initial_library?: unknown; mulligan_count?: number },
    ): Promise<PlaytestTableData> {
      const res = await fetch('/api/v1/playtester/tables', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ deck_id, display_name, ...options }),
      });
      if (!res.ok) throw new Error(`create table failed: ${res.status}`);
      return res.json();
    },

    async get(table_id: string, seat_index = 0): Promise<PlaytestTableData | null> {
      const res = await fetch(`/api/v1/playtester/tables/${table_id}?seat_index=${seat_index}`);
      if (res.status === 404) return null;
      if (!res.ok) throw new Error(`get table failed: ${res.status}`);
      return res.json();
    },

    async submitEvent(
      table_id: string,
      event_type: string,
      seat_index: number,
      payload: Record<string, unknown>,
    ): Promise<TableEventResult> {
      const res = await fetch(`/api/v1/playtester/tables/${table_id}/events`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ event_type, seat_index, payload }),
      });
      if (!res.ok) throw new Error(`submitEvent failed: ${res.status}`);
      return res.json();
    },

    async end(table_id: string): Promise<void> {
      const res = await fetch(`/api/v1/playtester/tables/${table_id}/end`, { method: 'POST' });
      if (!res.ok) throw new Error(`end table failed: ${res.status}`);
    },

    wsUrl(table_id: string, seat_index = 0): string {
      const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      return `${proto}//${window.location.host}/api/v1/playtester/tables/${table_id}/ws?seat=${seat_index}`;
    },
  },
};

/**
 * Post a semantic event to a table.
 * The server broadcasts the updated ProjectedState over WebSocket after processing.
 */
export async function postEvent(
  tableId: string,
  seatIndex: number,
  event_type: string,
  payload: Record<string, unknown> = {},
): Promise<void> {
  const r = await apiFetch(`/api/playtester/tables/${tableId}/events`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ event_type, seat_index: seatIndex, payload }),
  })
  if (!r.ok) return apiError(r, `Failed to post event: ${event_type}`)
}

export type { CardData, ProjectedState, PostEventFn, LegalActions, ActiveChoice, TurnSlot, SeatZones } from '@/types/game'

// ─── Variants ─────────────────────────────────────────────────────────────────

export interface Overlay {
  cards: Record<string, number>;
  commanders: string[] | null;
}

export interface Build {
  id: string;                 // 'base' or a variant id
  kind: 'base' | 'variant';
  name: string;
  visibility?: 'public' | 'unlisted' | 'private';
  is_main: boolean;
  intent?: string | null;
  overlay?: Overlay;
  wotc_bracket?: number | null;
  /** The bracket this build is BUILT toward (overlay state); null on the base deck. */
  bracket_override?: number | null;
  minimum_bracket?: number | null;
  floor_reasons?: FloorReason[] | null;
  plays_like_reasons?: PlaysLikeReason[] | null;
  bracket_position?: BracketPosition | null;
  bracket_declaration?: BracketDeclarationOutcome | null;
  total_cards?: number;
  unique_cards?: number;
  // false when the variant's overlay no longer applies to the live base (its commander
  // changed under it): the listing still returns the build, with null totals/bracket.
  resolvable?: boolean;
}

export interface VariantDetail extends Build {
  parent_deck_id: string;
  parent_name?: string;
  commanders: string[];
  cards: Record<string, CardEntry>;   // resolved list
}

export type VariantVisibility = 'public' | 'unlisted' | 'private';

export interface CreateVariantBody {
  name: string;
  intent?: string;
  overlay?: Overlay;
  visibility?: VariantVisibility;
  /** Target bracket this variant is built toward (1–5). */
  bracket_override?: number;
  /** This variant's own goals; omitted = it falls back to the deck's. An empty envelope blocks the fallback. */
  goals?: GoalEnvelope | Record<string, never>;
}

export const variants = {
  async getBuilds(deckId: string): Promise<Build[]> {
    const r = await apiFetch(`/api/v1/decks/${deckId}/builds`);
    const data = await ok<{ deck_id: string; builds: Build[] }>(r, 'Failed to load builds');
    return data.builds;
  },
  async create(deckId: string, body: CreateVariantBody): Promise<{ id: string }> {
    const r = await apiFetch(`/api/v1/decks/${deckId}/variants`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
    });
    return ok<{ id: string }>(r, 'Failed to create variant');
  },
  async get(variantId: string): Promise<VariantDetail> {
    const r = await apiFetch(`/api/v1/variants/${variantId}`);
    return ok<VariantDetail>(r, 'Variant not found');
  },
  async update(variantId: string, patch: Partial<CreateVariantBody>): Promise<void> {
    const r = await apiFetch(`/api/v1/variants/${variantId}`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(patch),
    });
    await ok<unknown>(r, 'Failed to update variant');
  },
  async setVisibility(variantId: string, visibility: VariantVisibility): Promise<void> {
    const r = await apiFetch(`/api/v1/variants/${variantId}/visibility`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ visibility }),
    });
    await ok<unknown>(r, 'Failed to set visibility');
  },
  async remove(variantId: string): Promise<void> {
    const r = await apiFetch(`/api/v1/variants/${variantId}`, { method: 'DELETE' });
    await ok<unknown>(r, 'Failed to delete variant');
  },
  async diffSnapshots(variantId: string, s1: number, s2: number): Promise<DiffData> {
    const r = await apiFetch(`/api/v1/variants/${variantId}/diff/${s1}/${s2}`);
    return ok<DiffData>(r, 'Failed to diff revisions');
  },
  async setMainBuild(deckId: string, buildId: string | null): Promise<void> {
    const r = await apiFetch(`/api/v1/decks/${deckId}/main-build`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ build_id: buildId }),
    });
    await ok<unknown>(r, 'Failed to set main build');
  },
  async materialize(variantId: string): Promise<{ deck_id: string }> {
    const r = await apiFetch(`/api/v1/variants/${variantId}/materialize`, { method: 'POST' });
    return ok<{ deck_id: string }>(r, 'Failed to materialize variant');
  },
  async getAnalysis(variantId: string): Promise<{ delta_primer: { text: string }; diff: unknown }> {
    const r = await apiFetch(`/api/v1/variants/${variantId}/analysis`);
    return ok(r, 'Failed to load variant analysis');
  },
  async getPower(variantId: string): Promise<BuildBracketFields> {
    const r = await apiFetch(`/api/v1/variants/${variantId}/power`);
    return ok(r, 'Failed to load variant power level');
  },
  async getCardMetadata(variantId: string): Promise<{ card_metadata: Record<string, CardMeta> }> {
    const r = await apiFetch(`/api/v1/variants/${variantId}/card-metadata`);
    return ok<{ card_metadata: Record<string, CardMeta> }>(r, 'Failed to get variant card metadata');
  },
  async getTagGroups(variantId: string): Promise<TagGroup[]> {
    const r = await apiFetch(`/api/v1/variants/${variantId}/tag-groups`);
    return (await ok<{ groups: TagGroup[] }>(r, 'Failed to get variant tag groups')).groups;
  },
};

